// runtime-overlay.js — the main 16:9 broadcast overlay renderer (drawOverlay):
// score bug, batter/bowler strips, momentum/partnership panels, every event
// animation (4/6/wicket/milestone/intro sequence) and the full-screen boards.
// One frame per call from the runtime-camera draw loop. Split from runtime-core.js.
import { activeMatch, fullScreenBoard, lastBallState } from './svelte/store.js';
import {
  runRate, reqRate, currentInnings, currentOverBalls, currentOverSummary, formatOvers,
  getMatchPhase, getConsecutiveDots, getMomentumData, getLast5OversRR
} from './scoring-logic.js';
import { getActiveAnimation, advanceAnimation } from './broadcast/overlay-state.js';
import { getOverlayTheme, getOverlayTitle, getOverlayLogoSrc } from './broadcast/overlay-themes.js';
import { introStageAt } from './intro-sequence.js';
import { clampGradientStop } from './recording-utils.js';
import { getCurrentPartnership } from './match-stats.js';
import {
  _drawConfetti, _drawFirework, _drawStumps,
  easeOutElastic, easeOutBounce, easeOutBack
} from './runtime-anim.js';
import { getCachedLogo, getCachedPlayerPhoto, getCachedPlayerProfile } from './runtime-cache.js';
import {
  withCtx, rgba, clipText, drawBallPill, roundRect, sr, econ, getCachedIntroPayload
} from './runtime-draw-utils.js';
import { teamObj, teamColor, teamLogo, battingTeamLetter, bowlingTeamLetter } from './runtime-teams.js';
import { drawVerticalOverlay } from './runtime-overlay-vertical.js';
import { _drawInningsBreakBoard, _drawWinnerBoard, _drawMatchSummaryBoard } from './runtime-overlay-boards.js';
import { isLastManStanding } from './runtime-match-flow.js';

// Overlay performance mode default (battery-based downgrades are decoupled from
// the colour filter — see the draw loop in runtime-camera.js).
let _isLowPower = false;
let _activeMatch = null;
let _lastBallState = null;
let _fullScreenBoard = null;
activeMatch.subscribe(value => { _activeMatch = value; });
lastBallState.subscribe(value => { _lastBallState = value; });
fullScreenBoard.subscribe(value => { _fullScreenBoard = value; });

export function drawOverlay(ctx, W, H) {
  const m = _activeMatch;
  if (!m || !m.innings || m.innings.length === 0) return;
  const inn = currentInnings(m);
  if (!inn) return;

  if (W < H) {
    drawVerticalOverlay(ctx, W, H);
  } else {

  const battingTeam = teamObj(battingTeamLetter(inn));
  const bowlingTeam = teamObj(bowlingTeamLetter(inn));
  const battingColor = teamColor(battingTeam.name);
  const bowlingColor = teamColor(bowlingTeam.name);
  const striker = inn.batsmen.find(b => b.name === inn.currentStriker);
  const nonStriker = inn.batsmen.find(b => b.name === inn.currentNonStriker);
  const bowler = inn.bowlers.find(b => b.name === inn.currentBowler);
  const target = (m.innings.length === 2) ? m.innings[0].totalRuns + 1 : null;
  const challengeConfig = m.mode === 'challenge' && m.challenge?.type !== 'super_over' ? m.challenge : null;

  // ═══ FREE HIT pill (top-center, pulsing) ═══
  if (inn.freeHit) {
    withCtx(ctx, () => {
      const t = Date.now() / 600;
      ctx.globalAlpha = 0.78 + 0.22 * Math.sin(t);
      const fhW = 178, fhH = 32;
      ctx.shadowColor = "rgba(234,88,12,0.7)";
      ctx.shadowBlur = 18;
      ctx.fillStyle = "#ea580c";
      roundRect(ctx, (W - fhW) / 2, 22, fhW, fhH, 16); ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.fillStyle = "#fff";
      ctx.font = "900 13px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("● FREE HIT ●", W / 2, 22 + fhH / 2);
    });
  }

  const theme = getOverlayTheme();

  // ═══ TOURNAMENT TICKER (top-left, slim) — title customizable for PRO ═══
  withCtx(ctx, () => {
    const tx = 22, ty = 22, th = 30;
    const label = getOverlayTitle() + "   " + battingTeam.name.toUpperCase().slice(0, 14) + " vs " + bowlingTeam.name.toUpperCase().slice(0, 14);
    ctx.font = "900 11px Outfit, sans-serif";
    const tw = ctx.measureText(label).width + 28;
    ctx.fillStyle = "rgba(0,0,0,0.65)";
    roundRect(ctx, tx, ty, tw, th, 6); ctx.fill();
    ctx.fillStyle = theme.teamPanel ? battingColor : theme.accent;
    roundRect(ctx, tx, ty, 4, th, 6); ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(label, tx + 14, ty + th / 2 + 1);
  });

  // ═══ MATCH PHASE BADGE (below ticker — PowerPlay / Middle / Death) ═══
  if (theme.phaseBadge) {
    withCtx(ctx, () => {
      const phase = getMatchPhase(m.overs, inn.balls);
      const tx = 22, ty = 56, th = 22;
      const phaseLabel = phase.icon + " " + phase.name;
      ctx.font = "900 10px sans-serif";
      const tw = ctx.measureText(phaseLabel).width + 18;
      // Pulsing glow for Death Overs
      const pulse = phase.name === "DEATH OVERS" ? 0.85 + 0.15 * Math.sin(Date.now() / 300) : 1;
      ctx.globalAlpha = pulse;
      ctx.fillStyle = rgba(phase.color, 0.85);
      roundRect(ctx, tx, ty, tw, th, 4); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
      ctx.fillText(phaseLabel, tx + 9, ty + th / 2 + 1);
    });
  }

  // ═══ TOURNAMENT WATERMARK (top-right) — customizable / hideable for PRO ═══
  const logoSrc = getOverlayLogoSrc();
  const watermark = logoSrc ? getCachedLogo(logoSrc) : null;
  if (watermark) {
    withCtx(ctx, () => {
      ctx.globalAlpha = 0.85;
      ctx.shadowColor = "rgba(0,0,0,0.6)";
      ctx.shadowBlur = 12;
      ctx.drawImage(watermark, W - 102, 22, 80, 80);
    });
  }

  // ═══ MAIN BROADCAST BAR (bottom, IPL-style) ═══
  withCtx(ctx, () => {
    // Keep height low to maximize video visibility
    const stripH = 100;
    const stripY = H - stripH - 12;
    const stripX = 12;
    const stripW = W - 24;
    const radius = theme.radius;
    const accent = theme.accent;

    // ── COLUMN LAYOUT METRICS (proportional — fills full width) ──
    // Columns are seamless (no gap) for a sleek modern look.
    const col1W = Math.round(stripW * 0.10);  // 10% logo
    const col2W = Math.round(stripW * 0.22);  // 22% score
    const col3W = Math.round(stripW * 0.44);  // 44% players (inline stats)
    const col4W = stripW - col1W - col2W - col3W; // remainder 24% over/target
    const col1X = stripX;
    const col2X = col1X + col1W;
    const col3X = col2X + col2W;
    const col4X = col3X + col3W;

    // Draw the main unified glass block background
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 8;
    const bg = ctx.createLinearGradient(stripX, stripY, stripX, stripY + stripH);
    bg.addColorStop(0, theme.barTop);
    bg.addColorStop(1, theme.barBottom);
    ctx.fillStyle = bg;
    roundRect(ctx, stripX, stripY, stripW, stripH, radius);
    ctx.fill();
    ctx.shadowColor = "transparent";

    // Top edge gradient for the whole bar
    ctx.save();
    ctx.beginPath();
    roundRect(ctx, stripX, stripY, stripW, stripH, radius);
    ctx.clip();
    
    // Team-colored background for Col 1
    if (theme.teamPanel) {
      const col1Grad = ctx.createLinearGradient(col1X, stripY, col1X + col1W, stripY + stripH);
      col1Grad.addColorStop(0, rgba(battingColor, 0.9));
      col1Grad.addColorStop(1, rgba(battingColor, 0.4));
      ctx.fillStyle = col1Grad;
      ctx.fillRect(col1X, stripY, col1W, stripH);
    }

    // Accent line at the very top
    if (theme.topLine) {
      const topLine = ctx.createLinearGradient(stripX, stripY, stripX + stripW, stripY);
      topLine.addColorStop(0, theme.teamPanel ? battingColor : accent);
      topLine.addColorStop(0.5, "#fff");
      topLine.addColorStop(1, theme.teamPanel ? bowlingColor : accent);
      ctx.fillStyle = topLine;
      ctx.fillRect(stripX, stripY, stripW, 3);
    }

    // Slow shimmer sweep across the whole bar — broadcast "glass" feel.
    // Skipped in low-power mode (it's a full-width gradient fill per frame).
    if (!_isLowPower && theme.shimmer) {
      const shimX = stripX + ((Date.now() / 16) % (stripW + 300)) - 150;
      const shim = ctx.createLinearGradient(shimX - 110, 0, shimX + 110, 0);
      shim.addColorStop(0, "rgba(255,255,255,0)");
      shim.addColorStop(0.5, "rgba(255,255,255,0.05)");
      shim.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = shim;
      ctx.fillRect(stripX, stripY, stripW, stripH);
    }
    ctx.restore();

    // Subtle dividers
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(col2X, stripY + 15); ctx.lineTo(col2X, stripY + stripH - 15);
    ctx.moveTo(col3X, stripY + 15); ctx.lineTo(col3X, stripY + stripH - 15);
    ctx.moveTo(col4X, stripY + 15); ctx.lineTo(col4X, stripY + stripH - 15);
    ctx.stroke();

    // ── COLUMN 1: Logo & Initials ──
    const logoSize = Math.min(col1W - 20, stripH - 36);
    const logoX = col1X + (col1W - logoSize) / 2;
    const logoY = stripY + (stripH - logoSize) / 2 - 8;
    const initials = battingTeam.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 3);
    const battingLogo = getCachedLogo(teamLogo(battingTeam.name));
    if (battingLogo) {
      withCtx(ctx, () => {
        ctx.beginPath();
        roundRect(ctx, logoX, logoY, logoSize, logoSize, 6);
        ctx.clip();
        ctx.fillStyle = "#fff";
        ctx.fillRect(logoX, logoY, logoSize, logoSize);
        ctx.drawImage(battingLogo, logoX, logoY, logoSize, logoSize);
      });
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = 1;
      roundRect(ctx, logoX, logoY, logoSize, logoSize, 6); ctx.stroke();
    } else {
      withCtx(ctx, () => {
        ctx.beginPath();
        roundRect(ctx, logoX, logoY, logoSize, logoSize, 6);
        ctx.clip();
        const gLogo = ctx.createLinearGradient(logoX, logoY, logoX + logoSize, logoY + logoSize);
        gLogo.addColorStop(0, rgba(battingColor, 0.9));
        gLogo.addColorStop(1, rgba(battingColor, 0.5));
        ctx.fillStyle = gLogo;
        ctx.fillRect(logoX, logoY, logoSize, logoSize);
        ctx.fillStyle = "#fff";
        ctx.font = "900 16px Outfit, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(initials, logoX + logoSize / 2, logoY + logoSize / 2);
      });
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = 1;
      roundRect(ctx, logoX, logoY, logoSize, logoSize, 6); ctx.stroke();
    }

    // ── COLUMN 2: Big score block ──
    const padX2 = col2X + 16;
    // Team name
    ctx.fillStyle = accent;
    ctx.font = "900 13px Outfit, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(battingTeam.name.toUpperCase(), padX2, stripY + 24);
    
    // Score & Overs (Fixed scaling and vertical position)
    const scoreText = inn.totalRuns + "/" + inn.wickets;
    const oversText = "(" + formatOvers(inn.balls) + ")";
    
    // Dynamic cap for font size to prevent overlapping
    let scoreFontSize = 56;
    let oversFontSize = 20;
    while (scoreFontSize > 32) {
      ctx.font = "900 " + scoreFontSize + "px ui-monospace, monospace";
      const sw = ctx.measureText(scoreText).width;
      ctx.font = "900 " + oversFontSize + "px ui-monospace, monospace";
      const ow = ctx.measureText(oversText).width;
      if (sw + 8 + ow <= col2W - 24) break;
      scoreFontSize -= 2; oversFontSize = Math.max(16, oversFontSize - 1);
    }
    
    ctx.fillStyle = "#fff";
    ctx.font = "900 " + scoreFontSize + "px ui-monospace, monospace";
    ctx.fillText(scoreText, padX2, stripY + 72);
    const scoreW = ctx.measureText(scoreText).width;
    
    ctx.fillStyle = accent;
    ctx.font = "900 " + oversFontSize + "px ui-monospace, monospace";
    ctx.fillText(oversText, padX2 + scoreW + 8, stripY + 70);

    // CRR & Innings (Bottom line)
    ctx.fillStyle = "#a1a1aa";
    ctx.font = "800 12px sans-serif";
    let crrLine = "OVERS " + m.overs + "  •  CRR " + runRate(inn.totalRuns, inn.balls);
    if (m.innings.length === 1 && inn.balls > 0) {
      const proj = Math.floor((inn.totalRuns / inn.balls) * (m.overs * 6));
      crrLine += "  •  PROJ " + proj;
    }
    ctx.fillText(crrLine, padX2, stripY + 92);
    
    const innLabel = (m.innings.length === 1 ? "1st INN" : "2nd INN");
    ctx.textAlign = "right";
    ctx.fillStyle = "#71717a";
    ctx.fillText(innLabel, col2X + col2W - 16, stripY + 92);

    // ── COLUMN 3: Players (Striker, Non-striker, Bowler — compact inline layout) ──
    ctx.textAlign = "left";
    const padX3 = col3X + 20;
    const rightX3 = col3X + col3W - 20;
    const nameClipW = col3W * 0.35;
    
    const row1Y = stripY + 30;
    const row2Y = stripY + 60;
    const row3Y = stripY + 90;

    // Striker
    if (striker) {
      // Golden Triangle Indicator
      ctx.fillStyle = battingColor;
      ctx.beginPath();
      ctx.moveTo(padX3 - 10, row1Y - 10);
      ctx.lineTo(padX3 - 4, row1Y - 5);
      ctx.lineTo(padX3 - 10, row1Y);
      ctx.fill();

      ctx.fillStyle = "#fff";
      ctx.font = "900 18px Outfit, sans-serif";
      ctx.fillText(clipText(ctx, striker.name.toUpperCase(), nameClipW), padX3, row1Y);
      
      const runsText = striker.runs + "*";
      ctx.font = "900 20px ui-monospace, monospace";
      const rw = ctx.measureText(runsText).width;
      ctx.fillText(runsText, padX3 + nameClipW + 10, row1Y);
      
      ctx.fillStyle = "#a1a1aa";
      ctx.font = "700 14px ui-monospace, monospace";
      ctx.fillText("(" + striker.balls + ")", padX3 + nameClipW + 10 + rw + 6, row1Y - 1);
      
      // Inline Sub-stats right aligned
      ctx.textAlign = "right";
      ctx.fillStyle = "#71717a";
      ctx.font = "700 12px sans-serif";
      ctx.fillText("4s:" + striker.fours + "  6s:" + striker.sixes + "  SR:" + sr(striker.runs, striker.balls), rightX3, row1Y - 2);
    }
    
    // Non-striker
    if (nonStriker) {
      ctx.textAlign = "left";
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(padX3 - 7, row2Y - 5, 3, 0, Math.PI * 2); ctx.stroke();

      ctx.fillStyle = "#d4d4d8";
      ctx.font = "700 16px Outfit, sans-serif";
      ctx.fillText(clipText(ctx, nonStriker.name.toUpperCase(), nameClipW), padX3, row2Y);

      const runsText = String(nonStriker.runs);
      ctx.font = "800 18px ui-monospace, monospace";
      const rw = ctx.measureText(runsText).width;
      ctx.fillText(runsText, padX3 + nameClipW + 10, row2Y);

      ctx.fillStyle = "#71717a";
      ctx.font = "700 12px ui-monospace, monospace";
      ctx.fillText("(" + nonStriker.balls + ")", padX3 + nameClipW + 10 + rw + 6, row2Y - 1);
    } else if (striker && isLastManStanding(m, inn)) {
      // LMS: lone survivor — broadcast the situation on the empty end
      ctx.textAlign = "left";
      ctx.fillStyle = accent;
      ctx.font = "900 13px sans-serif";
      ctx.fillText("🛡 LAST MAN STANDS", padX3, row2Y);
    }

    // Divider before Bowler
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.beginPath();
    ctx.moveTo(padX3 - 10, row3Y - 16); ctx.lineTo(rightX3, row3Y - 16);
    ctx.stroke();

    // Bowler
    if (bowler) {
      ctx.textAlign = "left";
      ctx.fillStyle = bowlingColor;
      ctx.beginPath();
      ctx.moveTo(padX3 - 10, row3Y - 10);
      ctx.lineTo(padX3 - 4, row3Y - 5);
      ctx.lineTo(padX3 - 10, row3Y);
      ctx.fill();

      ctx.fillStyle = "#fff";
      ctx.font = "800 16px Outfit, sans-serif";
      ctx.fillText(clipText(ctx, bowler.name.toUpperCase(), nameClipW), padX3, row3Y);
      
      ctx.fillStyle = bowlingColor;
      ctx.font = "900 18px ui-monospace, monospace";
      const wktText = bowler.wickets + "-" + bowler.runs;
      const ww = ctx.measureText(wktText).width;
      ctx.fillText(wktText, padX3 + nameClipW + 10, row3Y);
      
      ctx.fillStyle = "#a1a1aa";
      ctx.font = "700 13px ui-monospace, monospace";
      ctx.fillText("(" + formatOvers(bowler.balls) + ")", padX3 + nameClipW + 10 + ww + 6, row3Y - 1);
      
      ctx.textAlign = "right";
      ctx.fillStyle = "#71717a";
      ctx.font = "700 12px sans-serif";
      ctx.fillText("ECON: " + econ(bowler.runs, bowler.balls), rightX3, row3Y - 1);
    }

    // ── COLUMN 4: This-over pills + Target/Extras ──
    const padX4 = col4X + 16;
    ctx.textAlign = "left";
    ctx.fillStyle = "#a1a1aa";
    ctx.font = "900 12px sans-serif";
    ctx.fillText("THIS OVER", padX4, stripY + 24);

    // Pills
    const pills = currentOverBalls(inn);
    const pillSize = 24, pillGap = 4;
    if (pills.length === 0) {
      ctx.fillStyle = "#525252";
      ctx.font = "italic 12px sans-serif";
      ctx.fillText("Start of over", padX4, stripY + 44);
    } else {
      let px = padX4 + pillSize / 2, py = stripY + 40;
      pills.forEach(b => {
        drawBallPill(ctx, px, py, pillSize, b);
        px += pillSize + pillGap;
      });
    }

    // Over summary
    const over = currentOverSummary(inn);
    if (pills.length > 0) {
      ctx.fillStyle = "#fff";
      ctx.font = "900 13px ui-monospace, monospace";
      ctx.fillText(over.runs + " runs", padX4, stripY + 62);
    }

    // Target/Required or Extras
    if (challengeConfig) {
      const c = challengeConfig;
      const ballsLeft = Math.max(0, m.overs * 6 - inn.balls);
      let challengeTargetText = "";
      let challengeValueText = `TARGET: ${c.targetValue}`;
      
      if (c.type === 'target_runs') {
        const needRuns = Math.max(0, c.targetValue - inn.totalRuns);
        challengeTargetText = `NEED ${needRuns} RUNS`;
      } else if (c.type === 'target_sixes') {
        const currentSixes = inn.batsmen.reduce((sum, b) => sum + b.sixes, 0);
        const needSixes = Math.max(0, c.targetValue - currentSixes);
        challengeTargetText = `NEED ${needSixes} SIXES`;
        challengeValueText = `TARGET: ${c.targetValue} SIXES`;
      } else if (c.type === 'target_wickets') {
        const needWkts = Math.max(0, c.targetValue - inn.wickets);
        challengeTargetText = `NEED ${needWkts} WKTS`;
        challengeValueText = `TARGET: ${c.targetValue} WKTS`;
      }

      const tbY = stripY + 68, tbH = 24, tbW = col4W - 32;

      ctx.fillStyle = rgba(accent, 0.12);
      roundRect(ctx, padX4, tbY, tbW, tbH, 4); ctx.fill();
      ctx.strokeStyle = rgba(accent, 0.3);
      roundRect(ctx, padX4, tbY, tbW, tbH, 4); ctx.stroke();

      ctx.fillStyle = accent;
      ctx.textAlign = "left";
      ctx.font = "900 11px sans-serif";
      ctx.fillText(challengeValueText, padX4 + 8, tbY + 16);

      ctx.fillStyle = "#fff";
      ctx.font = "900 13px ui-monospace, monospace";
      ctx.fillText(challengeTargetText, padX4 + Math.max(85, ctx.measureText(challengeValueText).width + 15), tbY + 17);
      
      ctx.fillStyle = "#e5e7eb";
      ctx.textAlign = "right";
      ctx.fillText(`${ballsLeft} BALLS`, padX4 + tbW - 8, tbY + 16);
    } else if (target) {
      const need = Math.max(0, target - inn.totalRuns);
      const ballsLeft = m.overs * 6 - inn.balls;
      const tbY = stripY + 68, tbH = 24, tbW = col4W - 32;

      // Required-rate pressure color: green = cruising, amber = tight, red = steep
      const rrr = ballsLeft > 0 ? (need / ballsLeft) * 6 : 99;
      const crr = inn.balls > 0 ? (inn.totalRuns / inn.balls) * 6 : 0;
      const rrColor = need === 0 ? "#22c55e" : (rrr <= crr + 0.5 ? "#22c55e" : (rrr <= crr + 2.5 ? "#fbbf24" : "#ef4444"));

      ctx.fillStyle = rgba(accent, 0.12);
      roundRect(ctx, padX4, tbY, tbW, tbH, 4); ctx.fill();
      ctx.strokeStyle = rgba(accent, 0.3);
      roundRect(ctx, padX4, tbY, tbW, tbH, 4); ctx.stroke();

      ctx.fillStyle = accent;
      ctx.font = "900 11px sans-serif";
      ctx.fillText("TARGET " + target, padX4 + 8, tbY + 16);

      ctx.fillStyle = "#fff";
      ctx.font = "900 13px ui-monospace, monospace";
      ctx.fillText("NEED " + need + " in " + ballsLeft + "b", padX4 + 75, tbY + 17);

      ctx.fillStyle = rrColor;
      ctx.textAlign = "right";
      ctx.fillText("RR " + reqRate(need, ballsLeft), padX4 + tbW - 8, tbY + 16);

      // Chase progress bar — how far through the target the batting side is
      const pbY = tbY + tbH + 4, pbH = 4;
      const chasePct = Math.max(0, Math.min(1, inn.totalRuns / Math.max(1, target)));
      ctx.fillStyle = "rgba(255,255,255,0.10)";
      roundRect(ctx, padX4, pbY, tbW, pbH, 2); ctx.fill();
      if (chasePct > 0) {
        const pGrad = ctx.createLinearGradient(padX4, pbY, padX4 + tbW, pbY);
        pGrad.addColorStop(0, rgba(battingColor, 0.9));
        pGrad.addColorStop(1, accent);
        ctx.fillStyle = pGrad;
        roundRect(ctx, padX4, pbY, Math.max(pbH, tbW * chasePct), pbH, 2); ctx.fill();
      }
    } else {
      ctx.font = "800 11px sans-serif";

      // Boundaries
      const fours = inn.batsmen.reduce((sum, b) => sum + b.fours, 0);
      const sixes = inn.batsmen.reduce((sum, b) => sum + b.sixes, 0);
      ctx.fillStyle = "#a1a1aa";
      ctx.fillText("BOUNDARIES:", padX4, stripY + 82);
      ctx.fillStyle = accent;
      ctx.fillText(`${fours}x4s   ${sixes}x6s`, padX4 + 75, stripY + 82);
      
      // Extras
      const ext = inn.extras;
      ctx.fillStyle = "#a1a1aa";
      ctx.fillText("EXTRAS:", padX4, stripY + 96);
      ctx.fillStyle = "#fff";
      ctx.fillText(`${ext.wide}wd  ${ext.nb}nb  ${ext.b}b  ${ext.lb}lb`, padX4 + 55, stripY + 96);
    }
  });

  // ═══ PARTNERSHIP BADGE (above main bar, left side) ═══
  // Multi-line breakdown so the viewer can see each batter's contribution +
  // partnership extras + total. Computed from the ballLog so it always matches
  // the actual runs scored since the last wicket (independent of stats totals).
  if (striker && nonStriker) {
    withCtx(ctx, () => {
      const part = getCurrentPartnership(inn);
      // Only show once any ball of the current partnership has been bowled.
      const anyDelivery = part.totalBalls > 0 || part.extras > 0;
      if (!anyDelivery) return;

      const s1 = striker, s2 = nonStriker;
      const s1Stats = part.players[s1.name] || { runs: 0, balls: 0 };
      const s2Stats = part.players[s2.name] || { runs: 0, balls: 0 };

      // Build the 3 lines we'll render.
      const headerText = "PARTNERSHIP " + (part.totalRuns) + " (" + part.totalBalls + ")";
      const line1 = s1.name + " " + s1Stats.runs + " (" + s1Stats.balls + ")";
      const line2 = s2.name + " " + s2Stats.runs + " (" + s2Stats.balls + ")";
      const line3 = "Extras " + part.extras;

      // Measure the widest line to size the panel.
      ctx.font = "800 10px sans-serif";
      const wHeader = ctx.measureText(headerText).width;
      ctx.font = "700 10px sans-serif";
      const wL1 = ctx.measureText(line1).width;
      const wL2 = ctx.measureText(line2).width;
      const wL3 = ctx.measureText(line3).width;
      const innerWidth = Math.max(wHeader, wL1, wL2, wL3);
      const panelW = innerWidth + 18;
      const panelH = 56;
      const px = 12, py = H - 100 - 12 - panelH;

      ctx.fillStyle = "rgba(0,0,0,0.78)";
      roundRect(ctx, px, py, panelW, panelH, 5); ctx.fill();

      ctx.textAlign = "left";
      ctx.textBaseline = "top";

      // Header (amber)
      ctx.fillStyle = "#fbbf24";
      ctx.font = "800 10px sans-serif";
      ctx.fillText(headerText, px + 9, py + 5);

      // Player lines (striker highlighted, non-striker dimmer)
      ctx.font = "700 10px sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.fillText(line1, px + 9, py + 19);
      ctx.fillStyle = "#cbd5e1";
      ctx.fillText(line2, px + 9, py + 31);

      // Extras
      ctx.fillStyle = "#94a3b8";
      ctx.fillText(line3, px + 9, py + 43);
    });
  }

  // ═══ MOMENTUM METER (above main bar, right side) ═══
  const momentum = getMomentumData(inn);
  if (momentum) {
    withCtx(ctx, () => {
      const mx = W - 180, my = H - 100 - 12 - 60;
      const mw = 160, mh = 50;
      ctx.fillStyle = "rgba(0,0,0,0.78)";
      roundRect(ctx, mx, my, mw, mh, 5); ctx.fill();

      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#fbbf24";
      ctx.font = "800 9px sans-serif";
      ctx.fillText("MOMENTUM", mx + 8, my + 5);

      // Gauge bar
      const barX = mx + 8, barY = my + 18, barW = mw - 16, barH = 8;
      ctx.fillStyle = "rgba(255,255,255,0.1)";
      roundRect(ctx, barX, barY, barW, barH, 3); ctx.fill();

      // Fill based on ratio (0.5 to 1.5 clamped)
      const fillRatio = Math.max(0, Math.min(1, (momentum.ratio - 0.5)));
      const fillColor = momentum.ratio >= 1.2 ? "#22c55e" : momentum.ratio >= 0.9 ? "#fbbf24" : "#ef4444";
      ctx.fillStyle = fillColor;
      roundRect(ctx, barX, barY, barW * fillRatio, barH, 3); ctx.fill();

      // Center marker
      ctx.fillStyle = "#fff";
      ctx.fillRect(barX + barW * 0.5 - 1, barY - 1, 2, barH + 2);

      // Labels
      ctx.font = "700 9px sans-serif";
      ctx.textAlign = "left";
      ctx.fillStyle = "#a1a1aa";
      ctx.fillText("L12: " + momentum.recentRR, mx + 8, my + 30);
      ctx.textAlign = "right";
      ctx.fillText("CRR: " + momentum.overallRR, mx + mw - 8, my + 30);

      // Direction arrow
      const arrow = momentum.ratio >= 1.15 ? "▲ ACCELERATING" : momentum.ratio <= 0.85 ? "▼ SLOWING" : "● STEADY";
      const arrowColor = momentum.ratio >= 1.15 ? "#22c55e" : momentum.ratio <= 0.85 ? "#ef4444" : "#a1a1aa";
      ctx.textAlign = "center";
      ctx.fillStyle = arrowColor;
      ctx.font = "800 8px sans-serif";
      ctx.fillText(arrow, mx + mw / 2, my + 42);
    });
  }

  // ═══ LAST 5 OVERS BADGE (above main bar, left-center) ═══
  const l5 = getLast5OversRR(inn);
  if (l5) {
    withCtx(ctx, () => {
      const lx = W - 180 - 105, ly = H - 100 - 12 - 40;
      const lw = 95, lh = 30;
      ctx.fillStyle = "rgba(0,0,0,0.78)";
      roundRect(ctx, lx, ly, lw, lh, 5); ctx.fill();
      ctx.fillStyle = "#a1a1aa";
      ctx.font = "800 8px sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("LAST " + l5.overs + " OV", lx + 7, ly + 4);
      ctx.fillStyle = "#fff";
      ctx.font = "900 11px ui-monospace, monospace";
      ctx.fillText(l5.runs + " RUNS", lx + 7, ly + 15);
      ctx.textAlign = "right";
      ctx.fillStyle = "#fbbf24";
      ctx.font = "900 11px ui-monospace, monospace";
      ctx.fillText(l5.rr, lx + lw - 7, ly + 15);
    });
  }

  // ═══ DOT BALL PRESSURE INDICATOR (top-center, pulsing red) ═══
  const dots = getConsecutiveDots(inn);
  if (dots >= 3) {
    withCtx(ctx, () => {
      const pulse = 0.7 + 0.3 * Math.sin(Date.now() / 250);
      ctx.globalAlpha = pulse;
      const dpLabel = "🔴 PRESSURE BUILDING • " + dots + " DOTS";
      ctx.font = "900 12px sans-serif";
      const dpW = ctx.measureText(dpLabel).width + 24;
      const dpX = W / 2 - dpW / 2, dpY = 62;
      ctx.fillStyle = "rgba(220,38,38,0.85)";
      roundRect(ctx, dpX, dpY, dpW, 24, 6); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(dpLabel, W / 2, dpY + 12);
    });
  }
  }

  // ═══ AI COMMENTARY TICKER (lower third — burnt into the recording) ═══
  // This is the line the scorer sees in the app's CommentaryTicker; it now also
  // renders into the recorded video, which is where broadcast commentary
  // belongs. Sits above the badge row so it never collides with the
  // partnership / momentum / last-5 panels.
  if (_lastBallState?.commentary?.text) {
    const cElapsed = Date.now() - _lastBallState.ts;
    const C_HOLD = 5000;
    if (cElapsed <= C_HOLD) {
      withCtx(ctx, () => {
        const alpha = cElapsed < 250 ? cElapsed / 250
          : (cElapsed > C_HOLD - 600 ? (C_HOLD - cElapsed) / 600 : 1);
        ctx.globalAlpha = Math.max(0, Math.min(1, alpha));

        const stripH = 48;
        const stripY = H - 230;
        const stripX = 12;
        const stripW = W - 24;
        const labelW = 116;
        const accentColor = _lastBallState.color || "#38bdf8";

        const bg = ctx.createLinearGradient(stripX, stripY, stripX + stripW, stripY);
        bg.addColorStop(0, "rgba(7,17,31,0.94)");
        bg.addColorStop(1, "rgba(15,35,55,0.88)");
        ctx.fillStyle = bg;
        roundRect(ctx, stripX, stripY, stripW, stripH, 8); ctx.fill();
        ctx.strokeStyle = "rgba(56,189,248,0.30)";
        ctx.lineWidth = 1;
        roundRect(ctx, stripX, stripY, stripW, stripH, 8); ctx.stroke();
        ctx.fillStyle = accentColor;
        roundRect(ctx, stripX, stripY, 4, stripH, 3); ctx.fill();

        ctx.fillStyle = "rgba(56,189,248,0.14)";
        roundRect(ctx, stripX + 10, stripY + 10, labelW - 10, 28, 6); ctx.fill();
        ctx.fillStyle = "#7dd3fc";
        ctx.font = "900 10px Outfit, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText("LIVE COMMENTARY", stripX + 20, stripY + stripH / 2);

        const text = String(_lastBallState.commentary.text)
          .replace(/^(?:Aakash Chopra|Ravi Shastri|Sidhu) style:\s*/, "");
        ctx.fillStyle = "#f8fafc";
        ctx.font = "700 13px Outfit, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(clipText(ctx, text, stripW - labelW - 28), stripX + labelW + 12, stripY + stripH / 2);
      });
    }
  }

  // ═══ LAST BALL RESULT FLASH (top-center, auto-fades after 2.5s) ═══
  if (_lastBallState) {
    const lbr = _lastBallState;
    const lbrElapsed = Date.now() - lbr.ts;
    const hasCommentary = !!_lastBallState?.commentary?.text;
    // Hold long enough for the commentary ticker above to finish its 5s window;
    // the flash itself still fades out at 2.5s below.
    if (lbrElapsed > 5000) {
      lastBallState.set(null);
    } else if (lbrElapsed > 2500 || hasCommentary) {
      // flash finished, commentary may still be showing — draw nothing here
    } else {
      withCtx(ctx, () => {
        let alpha = lbrElapsed < 200 ? lbrElapsed / 200 : (lbrElapsed > 2000 ? (2500 - lbrElapsed) / 500 : 1);
        ctx.globalAlpha = Math.max(0, alpha);
        const lblText = lbr.text;
        ctx.font = "900 16px sans-serif";
        const tw = ctx.measureText(lblText).width + 24;
        const lx = W / 2 - tw / 2, ly = H - 100 - 12 - 28;
        ctx.fillStyle = "rgba(0,0,0,0.8)";
        roundRect(ctx, lx, ly, tw, 22, 6); ctx.fill();
        ctx.fillStyle = lbr.color;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(lblText, W / 2, ly + 11);
      });
    }
  }

  // ═══ BIG ANIMATIONS (4, 6, W, STATS, NEW_BATTER) ═══
  if (getActiveAnimation()) {
    const anim = getActiveAnimation();
    const elapsed = Date.now() - anim.startTs;
    const duration = anim.duration || 2500;
    if (elapsed > duration) {
      advanceAnimation(Date.now());
    } else {
      let progress = elapsed / duration;
      
      if (anim.type === "WICKET_STATS") {
        withCtx(ctx, () => {
          let alpha = Math.min(1, progress * 4, (1 - progress) * 4); // fade in out
          ctx.globalAlpha = alpha;
          
          const cx = W / 2, cy = H / 2 - 40;
          const bw = 540, bh = 220;
          
          // Card BG
          ctx.shadowColor = "rgba(0,0,0,0.8)";
          ctx.shadowBlur = 30;
          ctx.fillStyle = "rgba(10,10,15,0.95)";
          roundRect(ctx, cx - bw/2, cy - bh/2, bw, bh, 16); ctx.fill();
          
          // Header Red Line
          ctx.fillStyle = "#dc2626";
          ctx.fillRect(cx - bw/2, cy - bh/2, bw, 6);
          
          ctx.shadowColor = "transparent";
          ctx.fillStyle = "#dc2626";
          ctx.font = "900 24px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText("WICKET FALL", cx, cy - bh/2 + 20);
          
          ctx.fillStyle = "#fff";
          ctx.font = "900 42px sans-serif";
          ctx.fillText(anim.stats.name.toUpperCase(), cx, cy - bh/2 + 55);
          
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 36px ui-monospace, monospace";
          ctx.fillText(`${anim.stats.runs} (${anim.stats.balls})`, cx, cy - bh/2 + 105);
          
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "800 16px sans-serif";
          ctx.fillText(`SR: ${anim.stats.sr}   4s: ${anim.stats.fours}   6s: ${anim.stats.sixes}`, cx, cy - bh/2 + 150);
          
          ctx.fillStyle = "#ef4444";
          ctx.font = "800 18px sans-serif";
          ctx.fillText(`${anim.stats.dismissal}   b. ${anim.stats.bowler}`, cx, cy - bh/2 + 180);
        });
      }
      else if (anim.type === "PLAYER_LIST") {
        withCtx(ctx, () => {
          let alpha = Math.min(1, progress * 4, (1 - progress) * 4);
          let scale = 1;
          if (progress < 0.1) scale = 0.8 + 0.2 * (progress / 0.1);
          
          ctx.globalAlpha = alpha;
          
          const cx = W / 2, cy = H * 0.42;
          const bw = 900, bh = 480;
          
          ctx.translate(cx, cy);
          ctx.scale(scale, scale);
          ctx.translate(-cx, -cy);
          
          ctx.shadowColor = "rgba(0,0,0,0.8)";
          ctx.shadowBlur = 30;
          ctx.fillStyle = "rgba(10,10,15,0.95)";
          roundRect(ctx, cx - bw/2, cy - bh/2, bw, bh, 16); ctx.fill();
          
          // Header Red Line
          const grad = ctx.createLinearGradient(cx - bw/2, cy - bh/2, cx + bw/2, cy - bh/2);
          grad.addColorStop(0, teamColor(anim.teamA.name));
          grad.addColorStop(1, teamColor(anim.teamB.name));
          ctx.fillStyle = grad;
          ctx.fillRect(cx - bw/2, cy - bh/2, bw, 6);
          
          ctx.shadowColor = "transparent";
          ctx.fillStyle = "#fff";
          ctx.font = "900 32px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText(anim.title, cx, cy - bh/2 + 25);
          
          const listY = cy - bh/2 + 90;
          
          // Team A
          ctx.textAlign = "center";
          ctx.fillStyle = teamColor(anim.teamA.name);
          ctx.font = "900 24px sans-serif";
          ctx.fillText(anim.teamA.name.toUpperCase(), cx - bw/4, listY);
          
          ctx.fillStyle = "#e5e7eb";
          ctx.font = "700 18px sans-serif";
          anim.teamA.players.slice(0, 11).forEach((p, i) => {
             ctx.fillText((i+1) + ". " + p, cx - bw/4, listY + 36 + i * 28);
          });
          
          // Team B
          ctx.fillStyle = teamColor(anim.teamB.name);
          ctx.font = "900 24px sans-serif";
          ctx.fillText(anim.teamB.name.toUpperCase(), cx + bw/4, listY);
          
          ctx.fillStyle = "#e5e7eb";
          ctx.font = "700 18px sans-serif";
          anim.teamB.players.slice(0, 11).forEach((p, i) => {
             ctx.fillText((i+1) + ". " + p, cx + bw/4, listY + 36 + i * 28);
          });
          
          // Divider
          ctx.strokeStyle = "rgba(255,255,255,0.1)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(cx, listY - 10);
          ctx.lineTo(cx, cy + bh/2 - 20);
          ctx.stroke();
        });
      }
      else if (anim.type === "OVER_SUMMARY") {
        withCtx(ctx, () => {
          let alpha = Math.min(1, progress * 5, (1 - progress) * 5);
          
          // Slide-in from bottom with elastic bounce
          let slideY = 0;
          if (progress < 0.15) {
            const t = progress / 0.15;
            slideY = (1 - easeOutBack(t)) * 250;
          } else if (progress > 0.88) {
            const t = (progress - 0.88) / 0.12;
            slideY = t * t * 250;
          }
          
          ctx.globalAlpha = alpha;
          
          const cx = W / 2, cy = H * 0.42 + slideY;
          const bw = 620, bh = 230;
          
          // Card background
          ctx.shadowColor = "rgba(0,0,0,0.9)";
          ctx.shadowBlur = 35;
          ctx.fillStyle = "rgba(10,10,15,0.96)";
          roundRect(ctx, cx - bw/2, cy - bh/2, bw, bh, 16); ctx.fill();
          
          // Header gradient line with shimmer
          const grad = ctx.createLinearGradient(cx - bw/2, cy - bh/2, cx + bw/2, cy - bh/2);
          grad.addColorStop(0, "#7e22ce");
          grad.addColorStop(0.5, "#a855f7");
          grad.addColorStop(1, "#3b82f6");
          ctx.fillStyle = grad;
          ctx.fillRect(cx - bw/2, cy - bh/2, bw, 6);
          
          // Gradient shimmer sweep across card
          const shimmerPos = (elapsed * 0.0004) % 2 - 0.5;
          ctx.save();
          ctx.globalAlpha = alpha * 0.06;
          const shimGrad = ctx.createLinearGradient(cx - bw/2, cy - bh/2, cx + bw/2, cy - bh/2);
          shimGrad.addColorStop(clampGradientStop(shimmerPos - 0.2), "rgba(255,255,255,0)");
          shimGrad.addColorStop(clampGradientStop(shimmerPos), "rgba(255,255,255,1)");
          shimGrad.addColorStop(clampGradientStop(shimmerPos + 0.2), "rgba(255,255,255,0)");
          ctx.fillStyle = shimGrad;
          roundRect(ctx, cx - bw/2, cy - bh/2, bw, bh, 16); ctx.fill();
          ctx.restore();
          
          ctx.shadowColor = "transparent";
          ctx.fillStyle = "#fff";
          ctx.font = "900 28px Outfit, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText("OVER COMPLETE", cx, cy - bh/2 + 25);
          
          // Animated counter for runs
          const counterProgress = Math.min(1, progress * 3);
          const displayRuns = Math.round(anim.runs * counterProgress);
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 38px ui-monospace, monospace";
          ctx.fillText(`${displayRuns} RUNS`, cx, cy - bh/2 + 70);
          if (anim.wickets > 0) {
            ctx.fillStyle = "#ef4444";
            ctx.font = "900 24px ui-monospace, monospace";
            const displayWickets = Math.round(anim.wickets * counterProgress);
            ctx.fillText(`+ ${displayWickets} WICKET${displayWickets > 1 ? "S" : ""}`, cx, cy - bh/2 + 115);
          }
          
          // Sequential pill reveal with stagger
          const pills = anim.pills || [];
          const pSize = 34, pGap = 8;
          const totalW = pills.length * pSize + (pills.length - 1) * pGap;
          let px = cx - totalW / 2 + pSize / 2;
          const py = cy - bh/2 + 165;
          pills.forEach((b, idx) => {
            const pillDelay = 0.15 + idx * 0.04;
            const pillProgress = Math.max(0, (progress - pillDelay) / 0.08);
            if (pillProgress <= 0) { px += pSize + pGap; return; }
            const pillScale = Math.min(1, easeOutBack(Math.min(1, pillProgress)));
            const pillAlpha = Math.min(1, pillProgress * 2);
            ctx.save();
            ctx.globalAlpha = alpha * pillAlpha;
            ctx.translate(px, py);
            ctx.scale(pillScale, pillScale);
            ctx.translate(-px, -py);
            drawBallPill(ctx, px, py, pSize, b);
            ctx.restore();
            px += pSize + pGap;
          });
          
          // Bowler stats at bottom
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "800 16px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(`b. ${anim.bowler}  •  ${anim.bowlerStats}`, cx, cy + bh/2 - 25);
        });
      }
      else if (anim.type === "FOW") {
        withCtx(ctx, () => {
          let alpha = Math.min(1, progress * 5, (1 - progress) * 5);
          
          // Slide-in from bottom
          let slideY = 0;
          if (progress < 0.12) {
            const t = progress / 0.12;
            slideY = (1 - easeOutBack(t)) * 300;
          } else if (progress > 0.90) {
            const t = (progress - 0.90) / 0.10;
            slideY = t * t * 300;
          }
          
          ctx.globalAlpha = alpha;
          const cx = W / 2, cy = H * 0.42 + slideY;
          const fow = anim.fow || [];
          const bh = Math.min(320, 70 + fow.length * 32);
          const bw = 500;
          
          // Card background
          ctx.shadowColor = "rgba(0,0,0,0.9)";
          ctx.shadowBlur = 35;
          ctx.fillStyle = "rgba(10,10,15,0.96)";
          roundRect(ctx, cx - bw/2, cy - bh/2, bw, bh, 16); ctx.fill();
          
          // Team-colored header line
          ctx.shadowColor = "transparent";
          ctx.fillStyle = anim.teamColor || "#dc2626";
          ctx.fillRect(cx - bw/2, cy - bh/2, bw, 5);
          
          // Title
          ctx.fillStyle = "#dc2626";
          ctx.font = "900 22px Outfit, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText(anim.title, cx, cy - bh/2 + 18);
          
          // Team name
          ctx.fillStyle = anim.teamColor || "#fff";
          ctx.font = "800 14px sans-serif";
          ctx.fillText(anim.teamName.toUpperCase(), cx, cy - bh/2 + 46);
          
          // FOW entries with staggered reveal
          const startY = cy - bh/2 + 70;
          fow.forEach((f, idx) => {
            const entryDelay = 0.1 + idx * 0.06;
            const entryProgress = Math.max(0, (progress - entryDelay) / 0.1);
            if (entryProgress <= 0) return;
            const entryAlpha = Math.min(1, entryProgress * 2);
            const slideX = (1 - Math.min(1, entryProgress)) * 60;
            
            ctx.save();
            ctx.globalAlpha = alpha * entryAlpha;
            const ey = startY + idx * 30;
            
            // Wicket number badge
            ctx.fillStyle = "rgba(220,38,38,0.3)";
            roundRect(ctx, cx - bw/2 + 30 + slideX, ey, 26, 22, 4); ctx.fill();
            ctx.fillStyle = "#ef4444";
            ctx.font = "900 12px ui-monospace, monospace";
            ctx.textAlign = "center";
            ctx.fillText(String(f.wicket), cx - bw/2 + 43 + slideX, ey + 14);
            
            // Score
            ctx.fillStyle = "#fff";
            ctx.font = "900 16px ui-monospace, monospace";
            ctx.textAlign = "left";
            ctx.fillText(f.score + "/" + f.wicket, cx - bw/2 + 70 + slideX, ey + 15);
            
            // Overs
            ctx.fillStyle = "#a1a1aa";
            ctx.font = "700 13px sans-serif";
            ctx.fillText("(" + f.overs + " ov)", cx - bw/2 + 160 + slideX, ey + 15);
            
            ctx.restore();
          });
        });
      }
      else if (anim.type === "PHASE_STATS") {
        withCtx(ctx, () => {
          let alpha = Math.min(1, progress * 5, (1 - progress) * 5);
          
          let slideY = 0;
          if (progress < 0.12) { slideY = (1 - easeOutBack(progress / 0.12)) * 300; } 
          else if (progress > 0.90) { slideY = Math.pow((progress - 0.90) / 0.10, 2) * 300; }
          
          ctx.globalAlpha = alpha;
          const cx = W / 2, cy = H * 0.42 + slideY;
          const stats = anim.stats || [];
          const bh = 220;
          const bw = 640;
          
          ctx.shadowColor = "rgba(0,0,0,0.9)";
          ctx.shadowBlur = 35;
          ctx.fillStyle = "rgba(10,10,15,0.96)";
          roundRect(ctx, cx - bw/2, cy - bh/2, bw, bh, 16); ctx.fill();
          
          ctx.shadowColor = "transparent";
          ctx.fillStyle = anim.teamColor || "#dc2626";
          ctx.fillRect(cx - bw/2, cy - bh/2, bw, 5);
          
          ctx.fillStyle = "#fff";
          ctx.font = "900 22px Outfit, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText(anim.title, cx, cy - bh/2 + 18);
          
          ctx.fillStyle = anim.teamColor || "#a1a1aa";
          ctx.font = "800 14px sans-serif";
          ctx.fillText(anim.teamName.toUpperCase(), cx, cy - bh/2 + 46);
          
          // Columns for each phase
          const colW = bw / 3;
          stats.forEach((st, idx) => {
            const entryDelay = 0.1 + idx * 0.1;
            const entryProgress = Math.max(0, (progress - entryDelay) / 0.1);
            if (entryProgress <= 0) return;
            const entryAlpha = Math.min(1, entryProgress * 2);
            
            ctx.save();
            ctx.globalAlpha = alpha * entryAlpha;
            const cxCol = cx - bw/2 + colW * idx + colW/2;
            
            ctx.fillStyle = st.color;
            ctx.font = "900 13px sans-serif";
            ctx.fillText(st.label, cxCol, cy - bh/2 + 90);
            
            ctx.fillStyle = "#fff";
            ctx.font = "900 32px ui-monospace, monospace";
            ctx.fillText(`${st.runs}/${st.wickets}`, cxCol, cy - bh/2 + 120);
            
            ctx.fillStyle = "#a1a1aa";
            ctx.font = "800 14px sans-serif";
            ctx.fillText(`RR: ${st.rr}  (${st.overs} ov)`, cxCol, cy - bh/2 + 160);
            
            if (idx < 2) {
              ctx.fillStyle = "rgba(255,255,255,0.1)";
              ctx.fillRect(cxCol + colW/2, cy - bh/2 + 85, 1, 90);
            }
            ctx.restore();
          });
        });
      }
      else if (anim.type === "BEST_SPELL") {
        withCtx(ctx, () => {
          let alpha = Math.min(1, progress * 5, (1 - progress) * 5);
          let slideY = 0;
          if (progress < 0.12) { slideY = (1 - easeOutBack(progress / 0.12)) * 300; } 
          else if (progress > 0.90) { slideY = Math.pow((progress - 0.90) / 0.10, 2) * 300; }
          
          ctx.globalAlpha = alpha;
          const cx = W / 2, cy = H * 0.42 + slideY;
          const bw = 560, bh = 140;
          
          ctx.shadowColor = "rgba(0,0,0,0.9)";
          ctx.shadowBlur = 35;
          const grad = ctx.createLinearGradient(cx - bw/2, cy - bh/2, cx + bw/2, cy + bh/2);
          grad.addColorStop(0, "rgba(20,20,25,0.96)");
          grad.addColorStop(1, "rgba(5,5,10,0.96)");
          ctx.fillStyle = grad;
          roundRect(ctx, cx - bw/2, cy - bh/2, bw, bh, 16); ctx.fill();
          
          ctx.shadowColor = "transparent";
          ctx.fillStyle = anim.teamColor || "#dc2626";
          ctx.fillRect(cx - bw/2, cy - bh/2, bw, 5);
          
          const b = anim.bowler;
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 14px Outfit, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText("⭐ " + anim.title + " ⭐", cx, cy - bh/2 + 15);
          
          ctx.fillStyle = "#fff";
          ctx.font = "900 32px Outfit, sans-serif";
          ctx.fillText(b.name.toUpperCase(), cx, cy - bh/2 + 45);
          
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "800 20px ui-monospace, monospace";
          ctx.fillText(`${b.wickets}-${b.runs}  (${formatOvers(b.balls)})  ECON: ${econ(b.runs, b.balls)}`, cx, cy - bh/2 + 90);
        });
      }
      else if (anim.type === "INTRO_SEQUENCE") {
        const introPayload = getCachedIntroPayload(anim);
        const stage = introPayload ? introStageAt(elapsed) : null;
        if (stage) withCtx(ctx, () => {
          const payload = introPayload;
          const fade = Math.max(0, Math.min(1, stage.progress * 7, (1 - stage.progress) * 7));
          // Use a springier easing for a "smash" broadcast effect
          const eased = 1 - Math.pow(1 - Math.min(1, stage.progress * 1.4), 4);
          const cx = W / 2;
          ctx.globalAlpha = fade;

          // Replace solid black with a sleek lower-third transparent gradient
          const lowerThirdY = H * 0.45;
          const bgHeight = H * 0.55;
          const bg = ctx.createLinearGradient(0, lowerThirdY, 0, H);
          bg.addColorStop(0, "rgba(2,6,23,0)");
          bg.addColorStop(0.3, "rgba(2,6,23,0.85)");
          bg.addColorStop(1, "rgba(2,6,23,0.98)");
          ctx.fillStyle = bg;
          ctx.fillRect(0, lowerThirdY, W, bgHeight);
          
          // Accent bars at the top of the lower third
          ctx.fillStyle = payload.teamA.color;
          ctx.fillRect(0, lowerThirdY, W / 2, 5);
          ctx.fillStyle = payload.teamB.color;
          ctx.fillRect(W / 2, lowerThirdY, W / 2, 5);

          const drawTeamMark = (team, x, y, size) => {
            ctx.save();
            ctx.shadowColor = team.color;
            ctx.shadowBlur = 12; // Neon glow effect
            const logo = getCachedLogo(team.logo);
            if (logo) {
              ctx.drawImage(logo, x - size / 2, y - size / 2, size, size);
              ctx.restore();
              return;
            }
            ctx.fillStyle = team.color;
            roundRect(ctx, x - size / 2, y - size / 2, size, size, size * 0.18); ctx.fill();
            ctx.strokeStyle = "rgba(255,255,255,.75)";
            ctx.lineWidth = Math.max(2, size * 0.025);
            roundRect(ctx, x - size / 2, y - size / 2, size, size, size * 0.18); ctx.stroke();
            ctx.fillStyle = "#fff";
            ctx.shadowBlur = 0;
            ctx.font = `900 ${Math.round(size * 0.34)}px Outfit, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(team.name.split(/\s+/).map(word => word[0]).join("").slice(0, 3).toUpperCase(), x, y);
            ctx.restore();
          };

          if (stage.name === "match") {
            const slide = (1 - eased) * W * 0.22;
            const yCenter = H * 0.70;
            const logoSize = H * 0.18;
            drawTeamMark(payload.teamA, W * 0.30 - slide, yCenter, logoSize);
            drawTeamMark(payload.teamB, W * 0.70 + slide, yCenter, logoSize);
            ctx.textAlign = "center"; ctx.textBaseline = "middle";
            ctx.shadowColor = "#fbbf24"; ctx.shadowBlur = 10;
            ctx.fillStyle = "#fbbf24"; ctx.font = `900 ${Math.round(H * 0.08)}px Outfit, sans-serif`; ctx.fillText("VS", cx, yCenter);
            ctx.shadowBlur = 0;
            ctx.fillStyle = "#fff"; ctx.font = `900 ${Math.round(H * 0.04)}px Outfit, sans-serif`;
            const sideMaxW = W * 0.34; // keep clear of the central "VS"
            ctx.fillText(clipText(ctx, payload.teamA.name.toUpperCase(), sideMaxW), W * 0.30 - slide, yCenter + logoSize * 0.7);
            ctx.fillText(clipText(ctx, payload.teamB.name.toUpperCase(), sideMaxW), W * 0.70 + slide, yCenter + logoSize * 0.7);
            ctx.fillStyle = "#93c5fd"; ctx.font = `800 ${Math.round(H * 0.025)}px Outfit, sans-serif`;
            // Just under the accent bars — H*0.94 sat inside the scoreboard strip
            ctx.fillText(`${payload.overs} OVERS  |  ${payload.venue.toUpperCase()}`, cx, H * 0.50);
          } else if (stage.name === "teamA" || stage.name === "teamB") {
            const team = stage.name === "teamA" ? payload.teamA : payload.teamB;
            const listY = H * 0.58;
            drawTeamMark(team, W * 0.14, listY, H * 0.11);
            ctx.textAlign = "left"; ctx.textBaseline = "middle";
            // The header block (logo + "PLAYING XI" + team name) must stay left
            // of the player list, which starts at x = W*0.40 — a long team name
            // used to run straight across the player names.
            const headerX = W * 0.21;
            const headerMaxW = W * 0.40 - headerX - W * 0.02;
            ctx.fillStyle = "#fbbf24"; ctx.font = `800 ${Math.round(H * 0.022)}px Outfit, sans-serif`; ctx.fillText("PLAYING XI", headerX, listY - H * 0.03);
            ctx.fillStyle = "#fff";
            const teamTitle = team.name.toUpperCase();
            let nameSize = Math.round(H * 0.045);
            const minNameSize = Math.round(H * 0.026);
            ctx.font = `900 ${nameSize}px Outfit, sans-serif`;
            while (nameSize > minNameSize && ctx.measureText(teamTitle).width > headerMaxW) {
              nameSize -= 1;
              ctx.font = `900 ${nameSize}px Outfit, sans-serif`;
            }
            ctx.fillText(clipText(ctx, teamTitle, headerMaxW), headerX, listY + H * 0.02);
            // Two 6-row columns; players beyond 12 would land back on top of
            // the first column, so cap the roster.
            const roster = team.players.slice(0, 12);
            const staggerCount = Math.max(1, Math.ceil(stage.progress * roster.length * 1.25));
            roster.forEach((name, index) => {
              if (index >= staggerCount) return;
              const col = index >= 6 ? 1 : 0;
              const row = index % 6;
              const x = W * (col ? 0.65 : 0.40);
              // Rows must finish above the persistent scoreboard strip
              // (top edge at H - 112): last row lands at 0.81H.
              const y = H * 0.50 + row * H * 0.062;
              const colMaxW = (col ? W * 0.97 : W * 0.63) - x;
              ctx.fillStyle = team.color; ctx.shadowColor = team.color; ctx.shadowBlur = 5; ctx.fillRect(x - 14, y - 2, 6, 6); ctx.shadowBlur = 0;
              ctx.fillStyle = "#e2e8f0"; ctx.font = `750 ${Math.round(H * 0.030)}px Outfit, sans-serif`;
              ctx.fillText(clipText(ctx, `${index + 1}. ${name}${name === team.captain ? "  (C)" : ""}`, colMaxW), x, y);
            });
          } else if (stage.name === "toss") {
            const winner = payload.toss.winner === payload.teamA.name ? payload.teamA : payload.teamB;
            const tossY = H * 0.72;
            drawTeamMark(winner, W * 0.30, tossY, H * 0.16);
            ctx.textAlign = "left"; ctx.textBaseline = "middle";
            ctx.fillStyle = "#fbbf24"; ctx.font = `900 ${Math.round(H * 0.035)}px Outfit, sans-serif`; ctx.fillText("TOSS UPDATE", W * 0.42, tossY - H * 0.06);
            ctx.fillStyle = "#fff"; ctx.font = `900 ${Math.round(H * 0.06)}px Outfit, sans-serif`; ctx.fillText(clipText(ctx, winner.name.toUpperCase(), W * 0.55), W * 0.42, tossY + H * 0.01);
            ctx.fillStyle = "#cbd5e1"; ctx.font = `800 ${Math.round(H * 0.03)}px Outfit, sans-serif`; ctx.fillText(`WON THE TOSS & CHOSE TO ${payload.toss.choice}`, W * 0.42, tossY + H * 0.08);
          } else {
            const isBowler = stage.name === "bowler";
            const name = isBowler ? payload.bowler.name : (stage.name === "striker" ? payload.striker : payload.nonStriker);
            const role = isBowler ? "OPENING BOWLER" : (stage.name === "striker" ? "OPENING STRIKER" : "NON-STRIKER");
            const team = isBowler ? (payload.teamA.players.includes(name) ? payload.teamA : payload.teamB) : (payload.teamA.players.includes(name) ? payload.teamA : payload.teamB);
            const panelX = -W * 0.04 + eased * W * 0.10;
            const panelY = H * 0.60; // keep the panel clear of the scoreboard strip
            const panelW = W * 0.55;
            const panelH = H * 0.18;
            ctx.save();
            ctx.shadowColor = team.color; ctx.shadowBlur = 15;
            ctx.fillStyle = "rgba(2,6,23,.94)"; roundRect(ctx, panelX, panelY, panelW, panelH, 14); ctx.fill();
            ctx.restore();
            ctx.fillStyle = team.color; ctx.fillRect(panelX, panelY, 10, panelH);
            ctx.textAlign = "left"; ctx.textBaseline = "middle";
            ctx.fillStyle = "#fbbf24"; ctx.font = `800 ${Math.round(H * 0.025)}px Outfit, sans-serif`; ctx.fillText(role, panelX + 36, panelY + panelH * 0.31);
            ctx.fillStyle = "#fff"; ctx.font = `900 ${Math.round(H * 0.052)}px Outfit, sans-serif`; ctx.fillText(clipText(ctx, name.toUpperCase(), panelW - 72), panelX + 36, panelY + panelH * 0.66);
          }

          const sweepX = ((elapsed % 1800) / 1800) * (W + 240) - 120;
          const sweep = ctx.createLinearGradient(sweepX - 80, 0, sweepX + 80, 0);
          sweep.addColorStop(0, "rgba(255,255,255,0)"); sweep.addColorStop(0.5, "rgba(255,255,255,.15)"); sweep.addColorStop(1, "rgba(255,255,255,0)");
          ctx.fillStyle = sweep; ctx.fillRect(sweepX - 80, lowerThirdY, 160, bgHeight);
        });
      }
      else if (anim.type === "MATCH_INTRO") {
        // Initialize floating particles once
        if (!anim._bgParticles) {
          anim._bgParticles = [];
          for (let i = 0; i < 40; i++) {
            anim._bgParticles.push({
              x: Math.random() * 1280, y: Math.random() * 720,
              vx: (Math.random() - 0.5) * 30, vy: -10 - Math.random() * 20,
              r: 1 + Math.random() * 3, a: 0.2 + Math.random() * 0.5
            });
          }
        }
        withCtx(ctx, () => {
          let alpha = Math.min(1, progress * 8, (1 - progress) * 8);
          ctx.globalAlpha = Math.max(0, alpha);

          // Full-screen dark backdrop
          ctx.fillStyle = "rgba(5,5,10,0.97)";
          ctx.fillRect(0, 0, W, H);

          // ── Floating background particles ──
          anim._bgParticles.forEach(p => {
            p.x += p.vx * 0.016;
            p.y += p.vy * 0.016;
            if (p.y < -10) { p.y = H + 10; p.x = Math.random() * W; }
            if (p.x < -10) p.x = W + 10;
            if (p.x > W + 10) p.x = -10;
            ctx.save();
            ctx.globalAlpha = alpha * p.a * 0.6;
            ctx.fillStyle = "#fbbf24";
            ctx.shadowColor = "#fbbf24";
            ctx.shadowBlur = 6;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          });

          // Top/bottom accent strip
          const stripGrad = ctx.createLinearGradient(0, 0, W, 0);
          stripGrad.addColorStop(0, anim.teamAColor || "#0a7d28");
          stripGrad.addColorStop(1, anim.teamBColor || "#dc2626");
          ctx.fillStyle = stripGrad;
          ctx.fillRect(0, 0, W, 8);
          ctx.fillRect(0, H - 8, W, 8);

          const cx = W / 2;

          // League title at top
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 22px Outfit, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText("FFPL SEASON 3", cx, 30);
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "700 12px sans-serif";
          ctx.fillText("FLASH FIRE PREMIERE LEAGUE", cx, 56);

          // Header
          ctx.fillStyle = "#ffffff";
          ctx.font = "900 36px Outfit, sans-serif";
          ctx.fillText("TODAY'S MATCH", cx, 88);

          // ── Teams row with SLIDE-IN logos ──
          const teamY = H * 0.40;
          const logoSize = 110;
          const sideX = W * 0.18;
          const namesY = teamY + logoSize / 2 + 26;

          // Logo slide-in: logos come from sides
          let logoSlideA = 0, logoSlideB = 0;
          if (progress < 0.2) {
            const t = progress / 0.2;
            const ease = easeOutElastic(Math.min(1, t));
            logoSlideA = -(1 - ease) * (W * 0.4);
            logoSlideB = (1 - ease) * (W * 0.4);
          }

          const drawLogo = (logoUrl, color, lx, ly) => {
            const cached = getCachedLogo(logoUrl);
            if (cached) {
              ctx.drawImage(cached, lx - logoSize/2, ly - logoSize/2, logoSize, logoSize);
            } else {
              ctx.fillStyle = color || "#444";
              roundRect(ctx, lx - logoSize/2, ly - logoSize/2, logoSize, logoSize, 12); ctx.fill();
              ctx.strokeStyle = "#fff";
              ctx.lineWidth = 3;
              roundRect(ctx, lx - logoSize/2, ly - logoSize/2, logoSize, logoSize, 12); ctx.stroke();
            }
          };
          drawLogo(anim.teamALogo, anim.teamAColor, cx - sideX + logoSlideA, teamY);
          drawLogo(anim.teamBLogo, anim.teamBColor, cx + sideX + logoSlideB, teamY);

          // Team names
          ctx.font = "900 22px Outfit, sans-serif";
          ctx.textBaseline = "top";
          ctx.fillStyle = anim.teamAColor || "#fff";
          ctx.fillText(anim.teamAName.toUpperCase(), cx - sideX + logoSlideA, namesY);
          ctx.fillStyle = anim.teamBColor || "#fff";
          ctx.fillText(anim.teamBName.toUpperCase(), cx + sideX + logoSlideB, namesY);

          // ── Big "VS" with pulsing glow + subtle wobble ──
          ctx.save();
          const vsGlow = 30 + Math.sin(elapsed * 0.005) * 15;
          const vsWobble = Math.sin(elapsed * 0.003) * 0.02;
          ctx.translate(cx, teamY);
          ctx.rotate(vsWobble);
          ctx.shadowColor = "#fbbf24";
          ctx.shadowBlur = vsGlow;
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 90px Outfit, sans-serif";
          ctx.textBaseline = "middle";
          ctx.fillText("VS", 0, 0);
          ctx.restore();

          // ── Bottom pills with SLIDE UP entrance ──
          const pillsY = H * 0.78;
          const pillH = 56;
          let pillSlide = 0;
          if (progress < 0.35) {
            const t = Math.max(0, (progress - 0.15) / 0.2);
            pillSlide = (1 - easeOutBounce(Math.min(1, t))) * 120;
          }

          // Overs pill
          const oversText = anim.overs + " OVERS MATCH";
          ctx.font = "900 24px Outfit, sans-serif";
          const ow = ctx.measureText(oversText).width + 60;
          ctx.fillStyle = "rgba(251,191,36,0.18)";
          roundRect(ctx, cx - ow - 12, pillsY + pillSlide, ow, pillH, 8); ctx.fill();
          ctx.strokeStyle = "#fbbf24";
          ctx.lineWidth = 2;
          roundRect(ctx, cx - ow - 12, pillsY + pillSlide, ow, pillH, 8); ctx.stroke();
          ctx.fillStyle = "#fbbf24";
          ctx.textBaseline = "middle";
          ctx.fillText(oversText, cx - ow/2 - 12, pillsY + pillH/2 + pillSlide);

          // Venue pill (slightly more delayed)
          let pillSlide2 = 0;
          if (progress < 0.40) {
            const t = Math.max(0, (progress - 0.20) / 0.2);
            pillSlide2 = (1 - easeOutBounce(Math.min(1, t))) * 120;
          }
          const venueText = "📍 " + (anim.venue || "Rohini Sec 28");
          ctx.font = "900 22px Outfit, sans-serif";
          const vw = ctx.measureText(venueText).width + 60;
          ctx.fillStyle = "rgba(96,165,250,0.18)";
          roundRect(ctx, cx + 12, pillsY + pillSlide2, vw, pillH, 8); ctx.fill();
          ctx.strokeStyle = "#60a5fa";
          ctx.lineWidth = 2;
          roundRect(ctx, cx + 12, pillsY + pillSlide2, vw, pillH, 8); ctx.stroke();
          ctx.fillStyle = "#bfdbfe";
          ctx.fillText(venueText, cx + vw/2 + 12, pillsY + pillH/2 + pillSlide2);
        });
      }
      else if (anim.type === "MATCH_TOSS") {
        withCtx(ctx, () => {
          // Overall fade in/out for the whole card.
          const outerAlpha = Math.min(1, progress * 10, (1 - progress) * 10);
          ctx.globalAlpha = Math.max(0, outerAlpha);

          // Full-screen dark backdrop
          ctx.fillStyle = "rgba(5,5,10,0.96)";
          ctx.fillRect(0, 0, W, H);

          const cx = W / 2;
          const coinBaseY = H * 0.42;

          // Header — always visible
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 36px Outfit, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText("TOSS", cx, H * 0.12);
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "700 13px sans-serif";
          ctx.fillText("FLIPPING THE COIN…", cx, H * 0.12 + 44);

          // ─── Coin flight phases ───────────────────────────────────────
          // Phase 1 (0%..65%)  : Throw — parabolic arc, 6 fast rotations.
          // Phase 2 (65%..82%) : Land — y settles, rotation damps onto face-on.
          // Phase 3 (82%..100%): Reveal — coin holds face-on, winner card fades in below.
          const flipEnd = 0.65;
          const settleEnd = 0.82;
          const totalSpins = 6; // full 360° spins during the throw
          const peakLift = H * 0.32; // how high above ground it goes
          let coinY, rotation;

          if (progress < flipEnd) {
            const t = progress / flipEnd;
            coinY = coinBaseY - peakLift * Math.sin(t * Math.PI);
            rotation = t * Math.PI * 2 * totalSpins;
          } else if (progress < settleEnd) {
            const t = (progress - flipEnd) / (settleEnd - flipEnd);
            coinY = coinBaseY;
            // Snap rotation onto the nearest face-on multiple of PI, with a
            // damped sinusoidal wobble during the settle.
            const flipEndRot = flipEnd * Math.PI * 2 * totalSpins;
            const targetRot = Math.round(flipEndRot / Math.PI) * Math.PI;
            rotation = flipEndRot + (targetRot - flipEndRot) * t + Math.sin(t * Math.PI * 5) * (1 - t) * 0.35;
          } else {
            coinY = coinBaseY;
            const flipEndRot = flipEnd * Math.PI * 2 * totalSpins;
            rotation = Math.round(flipEndRot / Math.PI) * Math.PI;
          }

          const cosR = Math.cos(rotation);
          const flatness = Math.abs(cosR); // 1 = face-on, 0 = edge-on
          const isHeadsSide = cosR > 0;

          // ─── Ground shadow ────────────────────────────────────────────
          const heightAboveGround = (coinBaseY - coinY) / peakLift; // 0 on ground, 1 at peak
          const shadowAlpha = Math.max(0.15, 0.6 - heightAboveGround * 0.45);
          const shadowScale = Math.max(0.4, 1 - heightAboveGround * 0.6);
          ctx.save();
          ctx.globalAlpha = outerAlpha * shadowAlpha;
          ctx.fillStyle = "#000";
          ctx.beginPath();
          ctx.ellipse(cx, coinBaseY + 55, 78 * shadowScale, 14 * shadowScale, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          // ─── Coin body ────────────────────────────────────────────────
          const rx = 78;
          const ry = Math.max(2, 78 * flatness);

          ctx.save();
          ctx.translate(cx, coinY);

          // Rim — visible when coin is even slightly sideways (so we see the "side")
          if (flatness < 0.97) {
            ctx.fillStyle = "#78350f";
            ctx.beginPath();
            ctx.ellipse(0, 0, rx + 2, ry + 5, 0, 0, Math.PI * 2);
            ctx.fill();
          }

          // Face — radial gradient for shiny gold look. Heads = brighter gold,
          // Tails = paler gold, so the eye perceives the rotation even at speed.
          const faceGrad = ctx.createRadialGradient(0, -ry * 0.3, 0, 0, 0, rx);
          if (isHeadsSide) {
            faceGrad.addColorStop(0, "#fde68a");
            faceGrad.addColorStop(0.6, "#fbbf24");
            faceGrad.addColorStop(1, "#b45309");
          } else {
            faceGrad.addColorStop(0, "#fef3c7");
            faceGrad.addColorStop(0.6, "#eab308");
            faceGrad.addColorStop(1, "#854d0e");
          }
          ctx.fillStyle = faceGrad;
          ctx.beginPath();
          ctx.ellipse(0, 0, rx * 0.96, Math.max(2, ry * 0.96), 0, 0, Math.PI * 2);
          ctx.fill();

          // Face symbol - drawn vector shapes (Bat for Heads, Ball for Tails)
          if (flatness > 0.45) {
            const contentAlpha = (flatness - 0.45) / 0.55;
            ctx.save();
            ctx.globalAlpha = contentAlpha;
            if (isHeadsSide) {
              // Draw Cricket Bat
              ctx.scale(flatness, 1);
              ctx.translate(0, -6);
              ctx.rotate(Math.PI / 6); // slight angle
              ctx.fillStyle = "#854d0e"; // handle color
              ctx.fillRect(-3, -22, 6, 16);
              ctx.fillStyle = "#fef3c7"; // blade color
              ctx.shadowColor = "rgba(0,0,0,0.3)";
              ctx.shadowBlur = 4;
              ctx.beginPath();
              ctx.moveTo(-7, -6);
              ctx.lineTo(7, -6);
              ctx.lineTo(9, 20);
              ctx.quadraticCurveTo(0, 26, -9, 20);
              ctx.closePath();
              ctx.fill();
              ctx.shadowBlur = 0;
            } else {
              // Draw Cricket Ball
              ctx.scale(flatness, 1);
              ctx.fillStyle = "#b91c1c";
              ctx.shadowColor = "rgba(0,0,0,0.4)";
              ctx.shadowBlur = 6;
              ctx.beginPath();
              ctx.arc(0, 0, 18, 0, Math.PI * 2);
              ctx.fill();
              ctx.shadowBlur = 0;
              ctx.strokeStyle = "#fff";
              ctx.lineWidth = 1.5;
              ctx.setLineDash([2, 2]);
              ctx.beginPath();
              ctx.arc(-8, 0, 14, -Math.PI/2.2, Math.PI/2.2);
              ctx.stroke();
              ctx.beginPath();
              ctx.arc(8, 0, 14, Math.PI - Math.PI/2.2, Math.PI + Math.PI/2.2);
              ctx.stroke();
              ctx.setLineDash([]);
            }
            ctx.restore();
          }

          // Highlight streak — fakes a moving specular when spinning fast
          if (progress < flipEnd) {
            ctx.save();
            ctx.globalAlpha = 0.35 * flatness;
            const hg = ctx.createLinearGradient(-rx, -ry, rx, ry);
            hg.addColorStop(0, "rgba(255,255,255,0)");
            hg.addColorStop(0.5, "rgba(255,255,255,0.9)");
            hg.addColorStop(1, "rgba(255,255,255,0)");
            ctx.fillStyle = hg;
            ctx.beginPath();
            ctx.ellipse(0, 0, rx * 0.96, Math.max(2, ry * 0.96), 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }

          ctx.restore();

          // ── Sparkle trail behind coin during flight ──
          if (progress < flipEnd) {
            ctx.save();
            const trailN = 8;
            for (let i = 0; i < trailN; i++) {
              const age = i * 25;
              const trailT = Math.max(0, (elapsed - age) / (duration * flipEnd));
              if (trailT <= 0 || trailT >= 1) continue;
              const ty = coinBaseY - peakLift * Math.sin(trailT * Math.PI);
              const ta = Math.max(0, 0.4 - i * 0.05);
              ctx.globalAlpha = outerAlpha * ta;
              ctx.fillStyle = "#fbbf24";
              ctx.shadowColor = "#fbbf24";
              ctx.shadowBlur = 8;
              ctx.beginPath();
              ctx.arc(cx + (Math.random()-0.5)*6, ty + (Math.random()-0.5)*4, 3 - i*0.3, 0, Math.PI * 2);
              ctx.fill();
            }
            ctx.restore();
          }

          // ─── Result reveal (Phase 3) ─────────────────────────────────
          if (progress > settleEnd) {
            const revealT = (progress - settleEnd) / (1 - settleEnd);
            const revealAlpha = Math.min(1, revealT * 3);
            ctx.save();
            ctx.globalAlpha = outerAlpha * revealAlpha;
            ctx.textBaseline = "middle";

            // Winner team name, glowing in team color
            ctx.fillStyle = anim.color || "#fff";
            ctx.font = "900 60px Outfit, sans-serif";
            ctx.shadowColor = anim.color || "#fff";
            ctx.shadowBlur = 24;
            ctx.fillText(anim.winner.toUpperCase(), cx, H * 0.70);
            ctx.shadowBlur = 0;

            ctx.fillStyle = "#e5e7eb";
            ctx.font = "800 24px Outfit, sans-serif";
            ctx.fillText("WON THE TOSS", cx, H * 0.78);

            ctx.fillStyle = "#fbbf24";
            ctx.font = "900 36px Outfit, sans-serif";
            ctx.fillText("CHOSE TO " + anim.choice + " FIRST", cx, H * 0.86);
            ctx.restore();

            // ── Gold dust particles raining during reveal ──
            if (!anim._goldDust) {
              anim._goldDust = [];
              for (let i = 0; i < 25; i++) {
                anim._goldDust.push({
                  x: Math.random() * W, y: -Math.random() * H * 0.5,
                  vy: 30 + Math.random() * 60, vx: (Math.random()-0.5)*20,
                  r: 1 + Math.random() * 2.5, a: 0.3 + Math.random() * 0.5
                });
              }
            }
            ctx.save();
            anim._goldDust.forEach(p => {
              p.y += p.vy * 0.016;
              p.x += p.vx * 0.016;
              if (p.y > H) { p.y = -5; p.x = Math.random() * W; }
              ctx.globalAlpha = outerAlpha * revealAlpha * p.a;
              ctx.fillStyle = "#fbbf24";
              ctx.shadowColor = "#fbbf24";
              ctx.shadowBlur = 4;
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
              ctx.fill();
            });
            ctx.restore();
          }
        });
      }
      else if (anim.type === "MILESTONE") {
        // ── IPL-style batting milestone card (FIFTY / CENTURY) ──
        withCtx(ctx, () => {
          const alpha = Math.min(1, progress * 5, (1 - progress) * 5);
          ctx.globalAlpha = Math.max(0, alpha);
          const cx = W / 2, cy = H * 0.40;

          // Radial gold glow behind everything
          const glow = ctx.createRadialGradient(cx, cy, 30, cx, cy, 420);
          glow.addColorStop(0, rgba(anim.color || "#fbbf24", 0.28));
          glow.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = glow;
          ctx.fillRect(0, 0, W, H);

          // Confetti behind the card
          if (anim._confetti) _drawConfetti(ctx, anim._confetti, elapsed, duration);

          // Card pops in with elastic scale
          let scale = 1;
          if (progress < 0.18) scale = 0.6 + 0.4 * easeOutElastic(progress / 0.18);
          ctx.translate(cx, cy);
          ctx.scale(scale, scale);
          ctx.translate(-cx, -cy);

          const bw = 640, bh = 250;
          ctx.shadowColor = "rgba(0,0,0,0.9)";
          ctx.shadowBlur = 40;
          const bgGrad = ctx.createLinearGradient(cx, cy - bh / 2, cx, cy + bh / 2);
          bgGrad.addColorStop(0, "rgba(18,16,8,0.97)");
          bgGrad.addColorStop(1, "rgba(8,8,10,0.97)");
          ctx.fillStyle = bgGrad;
          roundRect(ctx, cx - bw / 2, cy - bh / 2, bw, bh, 18); ctx.fill();
          ctx.shadowColor = "transparent";

          // Gold frame + team-color top bar
          ctx.strokeStyle = rgba(anim.color || "#fbbf24", 0.85);
          ctx.lineWidth = 2;
          roundRect(ctx, cx - bw / 2, cy - bh / 2, bw, bh, 18); ctx.stroke();
          ctx.fillStyle = anim.teamColor || anim.color || "#fbbf24";
          ctx.fillRect(cx - bw / 2 + 4, cy - bh / 2, bw - 8, 6);

          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "900 14px Outfit, sans-serif";
          ctx.fillText("BATTING MILESTONE", cx, cy - bh / 2 + 20);

          // Big FIFTY!/CENTURY! with glow
          ctx.save();
          ctx.shadowColor = anim.color || "#fbbf24";
          ctx.shadowBlur = 28 + Math.sin(elapsed * 0.008) * 10;
          ctx.fillStyle = anim.color || "#fbbf24";
          ctx.font = "900 64px Outfit, sans-serif";
          ctx.fillText(anim.kind === "CENTURY" ? "💯 CENTURY!" : "🔥 FIFTY!", cx, cy - bh / 2 + 44);
          ctx.restore();

          // Player name (typing reveal)
          const nameText = (anim.name || "").toUpperCase();
          const chars = Math.min(nameText.length, Math.floor(elapsed / 40));
          ctx.fillStyle = "#fff";
          ctx.font = "900 34px Outfit, sans-serif";
          ctx.fillText(nameText.substring(0, chars), cx, cy - bh / 2 + 122);

          // Animated runs counter + stats
          const counterP = Math.min(1, Math.max(0, (progress - 0.15) / 0.3));
          const shownRuns = Math.round((anim.runs || 0) * counterP);
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 30px ui-monospace, monospace";
          ctx.fillText(shownRuns + "* (" + (anim.balls || 0) + ")", cx, cy - bh / 2 + 168);
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "800 16px sans-serif";
          ctx.fillText("4s: " + (anim.fours || 0) + "   6s: " + (anim.sixes || 0) + "   SR: " + sr(anim.runs || 0, anim.balls || 0), cx, cy - bh / 2 + 210);
        });
      }
      else if (anim.type === "WIN_PREDICTOR") {
        // ── WinViz-style chase predictor with animated split bar ──
        withCtx(ctx, () => {
          const alpha = Math.min(1, progress * 5, (1 - progress) * 5);
          ctx.globalAlpha = Math.max(0, alpha);
          let slideY = 0;
          if (progress < 0.12) slideY = (1 - easeOutBack(progress / 0.12)) * 300;
          else if (progress > 0.90) slideY = Math.pow((progress - 0.90) / 0.10, 2) * 300;

          const cx = W / 2, cy = H * 0.42 + slideY;
          const bw = 660, bh = 190;
          ctx.shadowColor = "rgba(0,0,0,0.9)";
          ctx.shadowBlur = 35;
          ctx.fillStyle = "rgba(10,10,15,0.96)";
          roundRect(ctx, cx - bw / 2, cy - bh / 2, bw, bh, 16); ctx.fill();
          ctx.shadowColor = "transparent";

          // Split-color top line
          const tl = ctx.createLinearGradient(cx - bw / 2, 0, cx + bw / 2, 0);
          tl.addColorStop(0, anim.batColor || "#22c55e");
          tl.addColorStop(1, anim.bowlColor || "#ef4444");
          ctx.fillStyle = tl;
          ctx.fillRect(cx - bw / 2, cy - bh / 2, bw, 5);

          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 22px Outfit, sans-serif";
          ctx.fillText("📊 WIN PREDICTOR", cx, cy - bh / 2 + 16);

          // Percentages count up together with the bar fill
          const fillP = Math.min(1, easeOutBack(Math.min(1, Math.max(0, (progress - 0.12) / 0.35))));
          const batPct = Math.round((anim.batPct || 50) * fillP);
          const bowlPct = Math.round((100 - (anim.batPct || 50)) * fillP);

          // Team names + live percentages
          ctx.font = "900 20px Outfit, sans-serif";
          ctx.textAlign = "left";
          ctx.fillStyle = anim.batColor || "#22c55e";
          ctx.fillText(clipText(ctx, (anim.batName || "").toUpperCase(), 220), cx - bw / 2 + 28, cy - bh / 2 + 56);
          ctx.textAlign = "right";
          ctx.fillStyle = anim.bowlColor || "#ef4444";
          ctx.fillText(clipText(ctx, (anim.bowlName || "").toUpperCase(), 220), cx + bw / 2 - 28, cy - bh / 2 + 56);

          ctx.font = "900 38px ui-monospace, monospace";
          ctx.textAlign = "left";
          ctx.fillStyle = "#fff";
          ctx.fillText(batPct + "%", cx - bw / 2 + 28, cy - bh / 2 + 82);
          ctx.textAlign = "right";
          ctx.fillText(bowlPct + "%", cx + bw / 2 - 28, cy - bh / 2 + 82);

          // The split bar itself
          const barX = cx - bw / 2 + 28, barW = bw - 56;
          const barY = cy + bh / 2 - 46, barH = 18;
          ctx.fillStyle = "rgba(255,255,255,0.08)";
          roundRect(ctx, barX, barY, barW, barH, 9); ctx.fill();
          const batW = barW * ((anim.batPct || 50) / 100) * Math.min(1, fillP);
          if (batW > 2) {
            ctx.fillStyle = anim.batColor || "#22c55e";
            roundRect(ctx, barX, barY, Math.max(barH, batW), barH, 9); ctx.fill();
          }
          const bowlW = barW * ((100 - (anim.batPct || 50)) / 100) * Math.min(1, fillP);
          if (bowlW > 2) {
            ctx.fillStyle = anim.bowlColor || "#ef4444";
            roundRect(ctx, barX + barW - Math.max(barH, bowlW), barY, Math.max(barH, bowlW), barH, 9); ctx.fill();
          }
          // Needle at the split point
          ctx.fillStyle = "#fff";
          ctx.fillRect(barX + barW * ((anim.batPct || 50) / 100) - 1.5, barY - 4, 3, barH + 8);
        });
      }
      else if (anim.type === "H2H") {
        // ── Head-to-head card: current striker vs current bowler ──
        withCtx(ctx, () => {
          const alpha = Math.min(1, progress * 5, (1 - progress) * 5);
          ctx.globalAlpha = Math.max(0, alpha);
          let slideY = 0;
          if (progress < 0.12) slideY = (1 - easeOutBack(progress / 0.12)) * 300;
          else if (progress > 0.90) slideY = Math.pow((progress - 0.90) / 0.10, 2) * 300;

          const cx = W / 2, cy = H * 0.42 + slideY;
          const bw = 660, bh = 170;
          ctx.shadowColor = "rgba(0,0,0,0.9)";
          ctx.shadowBlur = 35;
          ctx.fillStyle = "rgba(10,10,15,0.96)";
          roundRect(ctx, cx - bw / 2, cy - bh / 2, bw, bh, 16); ctx.fill();
          ctx.shadowColor = "transparent";

          const tl = ctx.createLinearGradient(cx - bw / 2, 0, cx + bw / 2, 0);
          tl.addColorStop(0, anim.batColor || "#3b82f6");
          tl.addColorStop(1, anim.bowlColor || "#dc2626");
          ctx.fillStyle = tl;
          ctx.fillRect(cx - bw / 2, cy - bh / 2, bw, 5);

          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillStyle = "#fbbf24";
          ctx.font = "900 18px Outfit, sans-serif";
          ctx.fillText("⚔ HEAD TO HEAD — THIS INNINGS", cx, cy - bh / 2 + 14);

          // Names with center VS
          ctx.font = "900 24px Outfit, sans-serif";
          ctx.textAlign = "right";
          ctx.fillStyle = anim.batColor || "#3b82f6";
          ctx.fillText(clipText(ctx, (anim.batName || "").toUpperCase(), 240), cx - 48, cy - bh / 2 + 52);
          ctx.textAlign = "left";
          ctx.fillStyle = anim.bowlColor || "#dc2626";
          ctx.fillText(clipText(ctx, (anim.bowlName || "").toUpperCase(), 240), cx + 48, cy - bh / 2 + 52);
          ctx.save();
          ctx.textAlign = "center";
          ctx.fillStyle = "#fbbf24";
          ctx.shadowColor = "#fbbf24";
          ctx.shadowBlur = 14;
          ctx.font = "900 26px Outfit, sans-serif";
          ctx.fillText("VS", cx, cy - bh / 2 + 50);
          ctx.restore();

          // Stats line
          ctx.textAlign = "center";
          ctx.fillStyle = "#fff";
          ctx.font = "900 26px ui-monospace, monospace";
          ctx.fillText((anim.runs || 0) + " runs off " + (anim.balls || 0) + " balls", cx, cy - bh / 2 + 96);
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "800 15px sans-serif";
          const outsTxt = anim.outs > 0 ? "   •   OUT " + anim.outs + "×" : "   •   NOT OUT";
          ctx.fillText("SR " + sr(anim.runs || 0, anim.balls || 0) + "   •   DOTS " + (anim.dots || 0) + outsTxt, cx, cy - bh / 2 + 134);
        });
      }
      else if (anim.type === "NEW_BATTER" || anim.type === "NEW_BOWLER" || anim.type === "BANNER") {
        withCtx(ctx, () => {
          let alpha = Math.min(1, progress * 5, (1 - progress) * 5);
          
          const isBowler = anim.type === "NEW_BOWLER";
          const isBanner = anim.type === "BANNER";
          
          const photo = (isBowler || !isBanner) ? getCachedPlayerPhoto(anim.name) : null;
          const hasPhoto = !!photo;
          
          const bw = isBanner ? 620 : 560;
          const bh = (isBowler || isBanner || hasPhoto) ? 90 : 66;
          const cx = W / 2;
          const cy = H * 0.42;

          // ── SLIDE-IN with elastic bounce ──
          let slideX = 0;
          if (progress < 0.2) {
            // Slide in from left (batter) or right (bowler)
            const t = progress / 0.2;
            const ease = easeOutElastic(t);
            const direction = isBowler ? 1 : -1;
            slideX = direction * (1 - ease) * (bw + 100);
          } else if (progress > 0.85) {
            // Slide out to opposite side
            const t = (progress - 0.85) / 0.15;
            const direction = isBowler ? -1 : 1;
            slideX = direction * t * t * (bw + 100);
          }

          const bx = cx - bw / 2 + slideX;
          const by = cy - bh / 2;
          
          ctx.globalAlpha = alpha;
          
          // ── Card shadow + background ──
          ctx.shadowColor = "rgba(0,0,0,0.9)";
          ctx.shadowBlur = 30;
          const grad = ctx.createLinearGradient(bx, by, bx+bw, by);
          if (isBowler) {
            grad.addColorStop(0, "rgba(220,38,38,0.94)");
            grad.addColorStop(1, "rgba(153,27,27,0.94)");
          } else if (isBanner) {
            grad.addColorStop(0, rgba(anim.color1, 0.95));
            grad.addColorStop(1, rgba(anim.color2, 0.95));
          } else {
            grad.addColorStop(0, "rgba(37,99,235,0.94)");
            grad.addColorStop(1, "rgba(29,78,216,0.94)");
          }
          ctx.fillStyle = grad;
          roundRect(ctx, bx, by, bw, bh, 14); ctx.fill();
          
          // ── Glowing pulsing accent line at top of card ──
          ctx.shadowColor = "transparent";
          const accentColor = isBowler ? "#fca5a5" : (isBanner ? "#fff" : "#93c5fd");
          const glowIntensity = 10 + Math.sin(elapsed * 0.008) * 6;
          ctx.save();
          ctx.shadowColor = accentColor;
          ctx.shadowBlur = glowIntensity;
          ctx.fillStyle = accentColor;
          ctx.fillRect(bx + 2, by, bw - 4, 3);
          ctx.restore();
          
          ctx.fillStyle = "#fff";
          
          if (isBanner) {
            ctx.font = "900 26px Outfit, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "top";
            ctx.fillText(anim.title, cx + slideX, by + 14);
            ctx.fillStyle = "#e5e7eb";
            ctx.font = "800 18px sans-serif";
            ctx.fillText(anim.text, cx + slideX, by + 50);
          } else if (isBowler) {
            if (hasPhoto) {
              // Draw circular photo on left
              ctx.save();
              ctx.beginPath();
              ctx.arc(bx + 50, by + 45, 30, 0, Math.PI * 2);
              ctx.clip();
              ctx.drawImage(photo, bx + 20, by + 15, 60, 60);
              ctx.restore();
              
              // Draw border ring
              ctx.strokeStyle = "#fca5a5";
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.arc(bx + 50, by + 45, 30, 0, Math.PI * 2);
              ctx.stroke();
              
              // Text left-aligned
              ctx.textAlign = "left";
              ctx.font = "900 24px Outfit, sans-serif";
              ctx.textBaseline = "top";
              const nameText = "🎳 " + anim.name.toUpperCase();
              ctx.fillText(nameText, bx + 100, by + 16);
              
              ctx.fillStyle = "#fca5a5";
              ctx.font = "800 15px sans-serif";
              if (progress > 0.25) {
                if (anim.overs !== "0.0" && anim.overs !== "0") {
                  ctx.fillText(`OVERS: ${anim.overs}  RUNS: ${anim.runs}  WKTS: ${anim.wickets}  ECON: ${anim.econ}`, bx + 100, by + 50);
                } else {
                  let careerMsg = "NEW BOWLER TO THE ATTACK";
                  try {
                    const player = getCachedPlayerProfile(anim.name);
                    if (player && player.stats && player.stats.matches > 0) {
                      careerMsg = `CAREER: ${player.stats.matches} matches • ${player.stats.wickets} wkts`;
                    }
                  } catch(_) {}
                  ctx.fillText(careerMsg, bx + 100, by + 50);
                }
              }
            } else {
              ctx.font = "900 26px Outfit, sans-serif";
              ctx.textAlign = "center";
              ctx.textBaseline = "top";
              const nameText = "🎳 " + anim.name.toUpperCase();
              const charsToShow = Math.min(nameText.length, Math.floor(elapsed / 40));
              ctx.fillText(nameText.substring(0, charsToShow), cx + slideX, by + 14);
              
              ctx.fillStyle = "#fca5a5";
              ctx.font = "800 16px sans-serif";
              if (progress > 0.25) {
                if (anim.overs !== "0.0" && anim.overs !== "0") {
                  ctx.fillText(`OVERS: ${anim.overs}   RUNS: ${anim.runs}   WICKETS: ${anim.wickets}   ECON: ${anim.econ}`, cx + slideX, by + 52);
                } else {
                  ctx.fillText("NEW BOWLER TO THE ATTACK", cx + slideX, by + 52);
                }
              }
            }
          } else {
            if (hasPhoto) {
              // Draw circular photo on left
              ctx.save();
              ctx.beginPath();
              ctx.arc(bx + 50, by + 45, 30, 0, Math.PI * 2);
              ctx.clip();
              ctx.drawImage(photo, bx + 20, by + 15, 60, 60);
              ctx.restore();
              
              // Draw border ring
              ctx.strokeStyle = "#93c5fd";
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.arc(bx + 50, by + 45, 30, 0, Math.PI * 2);
              ctx.stroke();
              
              // Text left-aligned
              ctx.textAlign = "left";
              ctx.font = "900 24px Outfit, sans-serif";
              ctx.textBaseline = "top";
              const nameText = "🏏 NEW BATTER";
              ctx.fillText(nameText, bx + 100, by + 16);
              
              ctx.fillStyle = "#93c5fd";
              ctx.font = "800 15px sans-serif";
              if (progress > 0.25) {
                let careerMsg = anim.name.toUpperCase();
                try {
                  const player = getCachedPlayerProfile(anim.name);
                  if (player && player.stats && player.stats.matches > 0) {
                    const srVal = player.stats.balls > 0 ? ((player.stats.runs / player.stats.balls) * 100).toFixed(1) : '0.0';
                    careerMsg = `${anim.name.toUpperCase()} (M: ${player.stats.matches} • R: ${player.stats.runs} • SR: ${srVal})`;
                  }
                } catch(_) {}
                ctx.fillText(careerMsg, bx + 100, by + 50);
              }
            } else {
              ctx.font = "900 26px Outfit, sans-serif";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              const nameText = "🏏 NEW BATTER: " + anim.name.toUpperCase();
              const charsToShow = Math.min(nameText.length, Math.floor(elapsed / 35));
              ctx.fillText(nameText.substring(0, charsToShow), cx + slideX, by + bh/2 + 2);
            }
          }
        });
      }
      else {
        // TEXT pops (4, 6, W, 50, 100, WD, NB)
        // Premium broadcast-grade animations with particles, shockwaves, and effects.
        let scale, alpha = 1;
        if (progress < 0.14) {
          const t = progress / 0.14;
          scale = 0.3 + easeOutBack(t) * 0.95;
        } else if (progress < 0.24) {
          const t = (progress - 0.14) / 0.10;
          scale = 1.25 - 0.25 * t;
        } else if (progress < 0.78) {
          const t = (progress - 0.24) / 0.54;
          scale = 1.0 + Math.sin(t * Math.PI * 2.6) * 0.022;
        } else {
          const t = (progress - 0.78) / 0.22;
          scale = 1.0 + t * 0.45;
          alpha = 1 - t * t;
        }

        const cx = W / 2;
        const cy = H * 0.42;
        const isShortToken = ["4", "6", "W"].includes(anim.type);

        // ── SCREEN FLASH (SIX only — bright white flash at start) ──
        if (anim.type === "6" && anim._flash && elapsed < 120 && !_isLowPower) {
          withCtx(ctx, () => {
            ctx.globalAlpha = Math.max(0, 0.3 * (1 - elapsed / 120));
            ctx.fillStyle = "#fff";
            ctx.fillRect(0, 0, W, H);
          });
        }

        // ── SCREEN SHAKE (WICKET only — canvas jitter for drama) ──
        if (anim.type === "W" && anim._shake && elapsed < 400) {
          const shakeDecay = 1 - elapsed / 400;
          const intensity = anim._shake.intensity * shakeDecay;
          ctx.save();
          ctx.translate(
            (Math.random() - 0.5) * intensity * 2,
            (Math.random() - 0.5) * intensity * 2
          );
        }

        // ── RED VIGNETTE (WICKET only — dramatic dark red edges) ──
        if (anim.type === "W" && isShortToken) {
          withCtx(ctx, () => {
            const vigAlpha = Math.min(1, progress * 6, (1 - progress) * 4) * 0.35;
            ctx.globalAlpha = vigAlpha;
            const vigGrad = ctx.createRadialGradient(cx, cy, H * 0.25, cx, cy, H * 0.9);
            vigGrad.addColorStop(0, "rgba(0,0,0,0)");
            vigGrad.addColorStop(1, "rgba(180,0,0,0.8)");
            ctx.fillStyle = vigGrad;
            ctx.fillRect(0, 0, W, H);
          });
        }

        // ── SHOCKWAVE RINGS (FOUR + SIX) ──
        if ((anim.type === "4" || anim.type === "6") && anim._shockwave) {
          withCtx(ctx, () => {
            const maxR = 350;
            const swProgress = Math.min(1, elapsed / 800);
            const r = swProgress * maxR;
            const swAlpha = (1 - swProgress) * 0.5 * alpha;
            ctx.globalAlpha = swAlpha;
            ctx.strokeStyle = anim.color;
            ctx.lineWidth = 4 - swProgress * 3;
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.stroke();
            // Second ring for SIX (slightly delayed)
            if (anim.type === "6" && elapsed > 150) {
              const sw2Progress = Math.min(1, (elapsed - 150) / 800);
              const r2 = sw2Progress * maxR;
              ctx.globalAlpha = (1 - sw2Progress) * 0.35 * alpha;
              ctx.beginPath();
              ctx.arc(cx, cy, r2, 0, Math.PI * 2);
              ctx.stroke();
            }
          });
        }

        // ── TRAIL STREAKS (FOUR — 8 fast light beams radiating out) ──
        if (anim.type === "4" && elapsed < 600) {
          withCtx(ctx, () => {
            const streakAlpha = Math.max(0, 1 - elapsed / 600) * 0.6 * alpha;
            ctx.globalAlpha = streakAlpha;
            for (let i = 0; i < 8; i++) {
              const ang = (i / 8) * Math.PI * 2 + 0.2;
              const innerR = 50 + elapsed * 0.2;
              const outerR = innerR + 80 + elapsed * 0.3;
              ctx.strokeStyle = anim.color;
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.moveTo(cx + Math.cos(ang) * innerR, cy + Math.sin(ang) * innerR);
              ctx.lineTo(cx + Math.cos(ang) * outerR, cy + Math.sin(ang) * outerR);
              ctx.stroke();
            }
          });
        }

        // ── CONFETTI EXPLOSION (FOUR + SIX particles) ──
        if (anim._confetti) {
          withCtx(ctx, () => {
            _drawConfetti(ctx, anim._confetti, elapsed, duration);
          });
        }

        // ── FIREWORK STARBURST (SIX only) ──
        if (anim._firework) {
          withCtx(ctx, () => {
            _drawFirework(ctx, anim._firework, elapsed, duration);
          });
        }

        // ── ROTATING SPARKLE RING (SIX only — orbiting glowing dots) ──
        if (anim.type === "6" && isShortToken) {
          withCtx(ctx, () => {
            ctx.globalAlpha = alpha * 0.8;
            const N = 12;
            const orbitR = 170;
            const rotAngle = elapsed * 0.003;
            for (let i = 0; i < N; i++) {
              const a2 = rotAngle + (i / N) * Math.PI * 2;
              const px = cx + Math.cos(a2) * orbitR;
              const py = cy + Math.sin(a2) * orbitR;
              ctx.shadowColor = anim.color;
              ctx.shadowBlur = 12;
              ctx.fillStyle = anim.color;
              ctx.beginPath();
              ctx.arc(px, py, 3 + Math.sin(elapsed * 0.01 + i) * 1.5, 0, Math.PI * 2);
              ctx.fill();
            }
          });
        }

        // ── FLYING STUMPS + BAILS (WICKET only) ──
        if (anim._stumps) {
          withCtx(ctx, () => {
            ctx.globalAlpha = alpha;
            _drawStumps(ctx, anim._stumps, elapsed);
          });
        }

        // ── Original sparkle particles (upgraded: more, brighter, with trails) ──
        if (isShortToken) {
          withCtx(ctx, () => {
            ctx.globalAlpha = alpha;
            const N = anim.type === "W" ? 12 : 20;
            for (let i = 0; i < N; i++) {
              const seedAng = (i / N) * Math.PI * 2 + (anim.type === "6" ? elapsed * 0.001 : 0);
              const dist = 60 + Math.min(elapsed / 5, 240);
              const fadeP = Math.min(1, elapsed / 1200);
              const a2 = (1 - fadeP) * 0.9;
              const px = cx + Math.cos(seedAng) * dist;
              const py = cy + Math.sin(seedAng) * dist - elapsed * 0.04;
              ctx.globalAlpha = alpha * a2;
              ctx.shadowColor = anim.color;
              ctx.shadowBlur = 22;
              ctx.fillStyle = anim.color;
              ctx.beginPath();
              ctx.arc(px, py, 5 - fadeP * 2.5, 0, Math.PI * 2);
              ctx.fill();
              // Sparkle trail
              if (elapsed < 800) {
                ctx.globalAlpha = alpha * a2 * 0.3;
                ctx.beginPath();
                ctx.arc(px - Math.cos(seedAng) * 8, py - Math.sin(seedAng) * 8 + 4, 3, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          });
        }

        // ── Halo ring + dark inner disc behind text ──
        if (isShortToken) {
          withCtx(ctx, () => {
            ctx.globalAlpha = alpha;
            ctx.translate(cx, cy);
            ctx.scale(scale, scale);

            // Outer glow ring — enhanced with pulsing glow
            ctx.shadowColor = anim.color;
            ctx.shadowBlur = 70 + Math.sin(Date.now() / 100) * 20;
            ctx.fillStyle = anim.color;
            ctx.beginPath();
            ctx.arc(0, 0, 145, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;

            // Inner dark disc
            ctx.fillStyle = "rgba(10,10,14,0.93)";
            ctx.beginPath();
            ctx.arc(0, 0, 130, 0, Math.PI * 2);
            ctx.fill();

            // Animated colored border — rotating dash effect
            ctx.strokeStyle = anim.color;
            ctx.lineWidth = 3;
            ctx.setLineDash([12, 8]);
            ctx.lineDashOffset = -elapsed * 0.08;
            ctx.beginPath();
            ctx.arc(0, 0, 130, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
          });
        }

        // ── Big text with stroke + glow ──
        withCtx(ctx, () => {
          ctx.globalAlpha = alpha;
          ctx.translate(cx, cy);
          ctx.scale(scale, scale);

          ctx.shadowColor = anim.color;
          ctx.shadowBlur = 40;
          ctx.fillStyle = anim.color;
          let fontSize = isShortToken ? 200 : 140;
          ctx.font = "900 " + fontSize + "px Outfit, sans-serif";
          let measured = ctx.measureText(anim.text).width;
          if (measured > W - 200) {
            fontSize = Math.floor(fontSize * ((W - 200) / measured));
            ctx.font = "900 " + fontSize + "px Outfit, sans-serif";
          }

          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          ctx.lineWidth = isShortToken ? 12 : 10;
          ctx.strokeStyle = "#fff";
          ctx.strokeText(anim.text, 0, 0);
          ctx.fillText(anim.text, 0, 0);

          // Sub-label
          let sub = null;
          if (isShortToken) {
            const subMap = { "4": "BOUNDARY", "6": "MAXIMUM", "W": "WICKET!" };
            sub = subMap[anim.type];
          } else if (anim.subText) {
            sub = anim.subText;
          }
          if (sub) {
            ctx.shadowBlur = 0;
            ctx.lineWidth = 5;
            const subFontSize = isShortToken ? 38 : 28;
            ctx.font = "900 " + subFontSize + "px Outfit, sans-serif";
            ctx.strokeStyle = "#0a0a0a";
            ctx.fillStyle = "#fff";
            const subY = isShortToken ? 175 : 110;
            ctx.strokeText(sub, 0, subY);
            ctx.fillText(sub, 0, subY);
          }
        });

        // ── Close screen shake transform (WICKET) ──
        if (anim.type === "W" && anim._shake && elapsed < 400) {
          ctx.restore();
        }
      }
    }
  }

  // ═══ FULL SCREEN SCOREBOARDS (IPL-style) ═══
  if (_fullScreenBoard) {
    withCtx(ctx, () => {
      const cx = W / 2;

      if (_fullScreenBoard === "innings1") {
        _drawInningsBreakBoard(ctx, W, H, cx, m);
      } else if (_fullScreenBoard === "winner") {
        _drawWinnerBoard(ctx, W, H, cx, m);
      } else if (_fullScreenBoard === "match") {
        _drawMatchSummaryBoard(ctx, W, H, cx, m);
      }
    });
  }
}
